import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Handshake,
  Users,
  ShieldCheck,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  MessageSquare,
  FileText,
  Loader2,
  Building2,
  Hash,
  Clock,
  CircleDot,
  CreditCard,
  Activity,
  Paperclip,
  Send,
  Download,
  UserCircle2,
  DollarSign,
  TrendingUp,
  Calendar,
  Mail,
  PhoneCall,
  FileSignature,
  ChevronDown,
  Award,
  Target,
  Gauge,
  HeartPulse,
  Flag,
  Zap,
  Receipt,
  Percent,
  ArrowDown,
  Landmark,
  BarChart3,
  BadgeCheck,
  Printer,
  Eye,
  Search,
  HardDrive,
  FolderOpen,
  Lock,
} from "lucide-react";
import { SiteHeader } from "@/components/site-header";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { SiteFooter } from "@/components/site-footer";
import { AuthGate } from "@/components/auth-gate";
import { useAuth } from "@/hooks/use-auth";
import { useRecoveryFacts } from "@/hooks/use-recovery-facts";
import {
  resolveNegotiationFinancial,
  supportedRecoveryDisplay,
} from "@/lib/financial/surface-financials";
import { useMyRealReviews, useRealReviewByFileNumber } from "@/hooks/use-real-reviews";
import { supabase } from "@/integrations/supabase/client";
import type { Negotiation, NegotiationMessage, Review, ReviewFile } from "@/lib/db/models";
import { requestExpertNegotiation } from "@/lib/negotiation-actions";
import { negotiationStages, negotiationService } from "@/lib/services/negotiationService";
import { LegalConsent } from "@/components/legal-consent";
import { NEGOTIATION_PRICE_LABEL, NEGOTIATION_PRICING_NOTE } from "@/lib/pricing";

const searchSchema = z.object({
  review: fallback(z.string().optional(), undefined),
});

export const Route = createFileRoute("/negotiation")({
  head: () => ({
    meta: [
      { title: "ClaimGuard | Full-Service Negotiation" },
      {
        name: "description",
        content:
          "Hand the invoice to our specialists. We negotiate directly with the vendor and share every update along the way.",
      },
    ],
  }),
  validateSearch: zodValidator(searchSchema),
  component: () => (
    <AuthGate>
      <NegotiationPage />
    </AuthGate>
  ),
});

function NegotiationPage() {
  const { review: reviewParam } = Route.useSearch();
  return (
    <div className="min-h-dvh bg-secondary/30">
      <SiteHeader />
      <div className="container-page space-y-14 py-12 md:py-20">
        <Breadcrumbs
          items={[
            { label: "Dashboard", to: "/dashboard" },
            { label: "Full-Service Negotiation" },
          ]}
        />
        {reviewParam ? <ReviewNegotiationView fileNumber={reviewParam} /> : <NegotiationOverview />}
      </div>
      <SiteFooter />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Default marketing overview + review picker                  */
/* ─────────────────────────────────────────────────────────── */

function NegotiationOverview() {
  const { user } = useAuth();
  const { reviews, loading } = useMyRealReviews();

  return (
    <>
      <header className="max-w-3xl">
        <div className="inline-flex items-center gap-2 rounded-full bg-purple-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-purple-700">
          <Handshake className="h-3.5 w-3.5" />
          Full-Service Negotiation
        </div>
        <h1 className="mt-4 font-display text-4xl leading-tight text-navy md:text-5xl">
          Let our specialists negotiate for you.
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">
          Experienced negotiation specialists take over after your Professional AI Review.
          {" "}
          {NEGOTIATION_PRICING_NOTE}
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/submit"
            className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-purple-700 hover:shadow-elevated"
          >
            Start Full-Service Negotiation
            <ArrowRight className="h-4 w-4" />
          </Link>
          <button
            type="button"
            onClick={() =>
              toast.success("Request received", {
                description: "A specialist will reach out within one business hour.",
              })
            }
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-white px-5 py-3 text-sm font-semibold text-navy transition hover:bg-white/60"
          >
            <MessageSquare className="h-4 w-4" />
            Talk to a specialist
          </button>
        </div>
      </header>

      {user && (
        <section className="rounded-2xl border border-border bg-white p-8 shadow-card">
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-foreground">
            Choose a review
          </div>
          <h2 className="mt-2 font-display text-2xl text-navy md:text-3xl">
            Pick the invoice you want us to handle.
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Select one of your submitted reviews to request Full-Service Negotiation. We'll pull the
            findings, contact the vendor, and update you at every step.
          </p>
          <div className="mt-6">
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading your reviews…
              </div>
            ) : reviews.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border bg-secondary/40 p-6 text-sm text-muted-foreground">
                You haven't submitted any invoices yet.{" "}
                <Link to="/submit" className="font-semibold text-navy hover:underline">
                  Submit your first invoice
                </Link>{" "}
                to unlock Full-Service Negotiation.
              </div>
            ) : (
              <ul className="grid gap-3 md:grid-cols-2">
                {reviews.map((r) => (
                  <li key={r.id}>
                    <Link
                      to="/negotiation"
                      search={{ review: r.file_number }}
                      className="flex items-center justify-between gap-3 rounded-xl border border-border bg-secondary/40 p-4 transition hover:-translate-y-0.5 hover:bg-white hover:shadow-card"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                          <Hash className="h-3 w-3" /> {r.file_number}
                        </div>
                        <div className="mt-1 truncate font-semibold text-navy">
                          {r.vendor ?? `Invoice ${r.file_number}`}
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          Status: {prettyStatus(r.status)}
                        </div>
                      </div>
                      <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}

      <section className="grid gap-5 md:grid-cols-3">
        {[
          {
            icon: <ShieldCheck className="h-5 w-5" />,
            title: "You stay in control",
            body: "Approve the strategy, review every message, and sign off before any settlement is accepted.",
          },
          {
            icon: <Sparkles className="h-5 w-5" />,
            title: "Backed by findings",
            body: "Our negotiators use your Professional Findings Report as the evidence base for every argument.",
          },
          {
            icon: <Users className="h-5 w-5" />,
            title: "Handled end-to-end",
            body: "We handle vendor calls, emails, and paperwork. You get status updates in your workspace.",
          },
        ].map((f) => (
          <div key={f.title} className="rounded-2xl border border-border bg-white p-6 shadow-card">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-purple-50 text-purple-700">
              {f.icon}
            </div>
            <div className="mt-4 font-display text-lg text-navy">{f.title}</div>
            <p className="mt-1.5 text-sm text-muted-foreground">{f.body}</p>
          </div>
        ))}
      </section>

      <section className="rounded-2xl border border-border bg-white p-8 shadow-card">
        <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-foreground">
          How it works
        </div>
        <h2 className="mt-2 font-display text-2xl text-navy md:text-3xl">
          From request to resolution.
        </h2>
        <ol className="mt-6 grid gap-5 md:grid-cols-4">
          {[
            "You request negotiation from your findings report.",
            "A specialist reviews the case and confirms the strategy.",
            "We contact the vendor and negotiate every line item.",
            "You approve the settlement and pay only if we save you money.",
          ].map((step, i) => (
            <li key={step} className="rounded-xl border border-border bg-secondary/40 p-5">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-navy text-sm font-semibold text-white">
                {i + 1}
              </div>
              <p className="mt-3 text-sm text-navy">{step}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-border bg-white p-6 shadow-card">
          <div className="flex items-center gap-2 text-sm font-semibold text-navy">
            <FileText className="h-4 w-4" /> Not sure yet?
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Open a sample Professional Findings Report to see the evidence our negotiators work
            from.
          </p>
          <Link
            to="/report/$id"
            params={{ id: "demo" }}
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:underline"
          >
            View sample report <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="rounded-2xl border border-border bg-white p-6 shadow-card">
          <div className="flex items-center gap-2 text-sm font-semibold text-navy">
            <CheckCircle2 className="h-4 w-4 text-savings" /> Already have a case?
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Head to your reviews and request full-service negotiation on any completed report.
          </p>
          <Link
            to="/dashboard"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:underline"
          >
            Open Dashboard <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Per-review request + status view                            */
/* ─────────────────────────────────────────────────────────── */

function ReviewNegotiationView({ fileNumber }: { fileNumber: string }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const bundle = useRealReviewByFileNumber(fileNumber);
  const [negotiation, setNegotiation] = useState<Negotiation | null>(null);
  const [negLoading, setNegLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [justRequested, setJustRequested] = useState(false);

  useEffect(() => {
    if (!bundle.review) {
      setNegotiation(null);
      setNegLoading(bundle.loading);
      return;
    }
    let cancelled = false;
    setNegLoading(true);
    supabase
      .from("negotiations")
      .select("*")
      .eq("review_id", bundle.review.id)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        setNegotiation((data as Negotiation | null) ?? null);
        setNegLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bundle.review?.id, bundle.loading]);

  const alreadyRequested =
    !!negotiation ||
    bundle.review?.status === "expert_negotiation_requested" ||
    bundle.review?.status === "negotiating" ||
    bundle.review?.status === "waiting_on_vendor" ||
    bundle.review?.status === "waiting_vendor" ||
    bundle.review?.status === "offer_received";

  const steps = useMemo(
    () => negotiationStages(negotiation, bundle.review?.status),
    [negotiation, bundle.review?.status],
  );

  /* SAB-001: the only financial input for this workspace — the linked review's
     canonical Total Supported Recovery (same resolver as report/dashboard). */
  const linkedReviewIds = useMemo(
    () => (bundle.review ? [bundle.review.id] : []),
    [bundle.review?.id],
  );
  const { facts: recoveryFacts } = useRecoveryFacts(linkedReviewIds);
  const canonicalRecovery = bundle.review
    ? (recoveryFacts.get(bundle.review.id)?.recovery ?? null)
    : null;

  async function handleRequest() {
    if (!bundle.review || !user) return;
    setSubmitting(true);
    try {
      const res = await requestExpertNegotiation(bundle.review, user.id);
      setNegotiation(res.negotiation);
      setJustRequested(res.created);
      if (res.created) {
        toast.success("Full-Service Negotiation requested", {
          description: "A specialist will be assigned shortly.",
        });
      } else {
        toast("Already requested", {
          description: "Showing your existing negotiation status.",
        });
      }
    } catch (e) {
      toast.error("Could not submit request", {
        description: (e as Error).message,
      });
    } finally {
      setSubmitting(false);
    }
  }

  if (bundle.loading || negLoading) {
    return (
      <div className="animate-pulse space-y-6" aria-busy="true" aria-label="Loading negotiation portal">
        <div className="flex flex-wrap items-center gap-3">
          <div className="h-6 w-48 rounded-full bg-secondary" />
          <div className="h-6 w-32 rounded-full bg-secondary" />
          <div className="h-6 w-36 rounded-full bg-secondary" />
        </div>
        <div className="h-56 rounded-3xl border border-border/60 bg-gradient-to-br from-secondary via-secondary/70 to-secondary" />
        <div className="grid gap-4 md:grid-cols-3">
          <div className="h-28 rounded-2xl bg-secondary" />
          <div className="h-28 rounded-2xl bg-secondary" />
          <div className="h-28 rounded-2xl bg-secondary" />
        </div>
        <div className="h-72 rounded-2xl bg-secondary" />
        <div className="sr-only">Loading your negotiation details…</div>
      </div>
    );
  }


  if (!bundle.review) {
    return (
      <div className="mx-auto max-w-2xl rounded-3xl border border-border bg-white p-8 shadow-card md:p-10">
        <div className="inline-flex items-center gap-2 rounded-full bg-purple-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-purple-700">
          <Handshake className="h-3.5 w-3.5" /> Full-Service Negotiation
        </div>
        <h1 className="mt-4 font-display text-3xl leading-tight text-navy md:text-4xl">
          We couldn't find that negotiation.
        </h1>
        <p className="mt-3 text-muted-foreground">
          The reference <span className="font-mono font-semibold text-navy">{fileNumber}</span> doesn't match
          any review on your account. Pick a claim from your dashboard to continue.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 rounded-lg bg-navy px-5 py-3 text-sm font-semibold text-white transition hover:bg-navy/90"
          >
            Open Dashboard
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/negotiation"
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-white px-5 py-3 text-sm font-semibold text-navy transition hover:bg-secondary"
          >
            Back to overview
          </Link>
        </div>
      </div>
    );
  }


  const r = bundle.review;
  /* Canonical only — no percentage-derived range. */
  const savings = supportedRecoveryDisplay(canonicalRecovery);

  const isCaseComplete =
    !!negotiation &&
    ["accepted", "settled", "completed"].includes((negotiation.status ?? "").toLowerCase());

  if (alreadyRequested) {
    return (
      <>
        {isCaseComplete ? (
          <NegotiationCompleteExperience
            review={r}
            negotiation={negotiation!}
            timeline={bundle.timeline}
            files={bundle.files}
          />
        ) : (
          <>
            <PremiumNegotiationHero
              review={r}
              negotiation={negotiation}
              timeline={bundle.timeline}
              steps={steps}
              savingsRange={savings}
              canonicalRecovery={canonicalRecovery}
              justRequested={justRequested}
            />

            <SavingsTracker
              review={r}
              negotiation={negotiation}
              canonicalRecovery={canonicalRecovery}
            />

            <NegotiationStatusPanel steps={steps} negotiation={negotiation} review={r} />
          </>
        )}
        <NegotiationActivityFeed
          timeline={bundle.timeline}
          negotiation={negotiation}
          review={r}
        />
        <NegotiationDocuments files={bundle.files} negotiation={negotiation} />
        {negotiation && user && (
          <div id="negotiation-messages" className="scroll-mt-24">
            <NegotiationMessageCenter
              negotiation={negotiation}
              reviewId={r.id}
              userId={user.id}
              role="customer"
            />
          </div>
        )}

        <NegotiationTeamSection negotiation={negotiation} />

        <div className="flex flex-wrap items-center gap-3 border-t border-border/60 pt-6">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 rounded-lg bg-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-navy/90"
          >
            <ArrowRight className="h-4 w-4 rotate-180" />
            Back to Dashboard
          </Link>
          <button
            type="button"
            onClick={() => navigate({ to: "/negotiation" })}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-white px-4 py-2.5 text-sm font-semibold text-navy transition hover:bg-secondary"
          >
            Full-Service Negotiation overview
          </button>
          <Link
            to="/support"
            className="ml-auto inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition hover:text-navy"
          >
            <MessageSquare className="h-3.5 w-3.5" />
            Contact Support
          </Link>
        </div>

      </>
    );
  }

  return (
    <PremiumRequestExperience
      review={r}
      savings={savings}
      canonicalRecovery={canonicalRecovery}
      submitting={submitting}
      onConfirm={handleRequest}
      onBack={() => navigate({ to: "/negotiation" })}
    />
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Negotiation Complete — celebration experience               */
/* ─────────────────────────────────────────────────────────── */

function NegotiationCompleteExperience({
  review,
  negotiation,
  timeline,
  files,
}: {
  review: Review;
  negotiation: Negotiation;
  timeline: import("@/lib/db/models").TimelineEvent[];
  files: ReviewFile[];
}) {
  const original = Number(negotiation.original_amount ?? review.total_amount ?? 0);
  const settlement = Number(
    negotiation.settlement_amount ?? negotiation.final_offer ?? original,
  );
  const totalSaved = Math.max(0, original - settlement);
  const percentSaved = original > 0 ? Math.round((totalSaved / original) * 100) : 0;

  const fmt = (n: number) => `$${Math.round(n).toLocaleString()}`;

  const specialist = negotiation.assigned_specialist_id ? "Sarah Morgan" : "Your specialist";
  const vendor = review.vendor ?? "Vendor";
  const settlementDate = negotiation.accepted_at ?? negotiation.updated_at;

  const createdAt = negotiation.created_at ? new Date(negotiation.created_at) : null;
  const closedAt = settlementDate ? new Date(settlementDate) : new Date();
  const days =
    createdAt != null
      ? Math.max(
          1,
          Math.round((closedAt.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24)),
        )
      : null;

  const lettersSent = timeline.filter((t) =>
    /letter|demand|outreach|vendor_contacted/i.test(t.event_type ?? ""),
  ).length;
  const totalComms = timeline.length;
  const docsGenerated = files.length + (negotiation.letter_body ? 1 : 0);

  const dateLabel = (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleDateString(undefined, {
          month: "long",
          day: "numeric",
          year: "numeric",
        })
      : "—";

  const packageDocs: Array<{ title: string; description: string; icon: typeof FileText }> = [
    {
      title: "Professional Findings Report",
      description: "Full AI-powered analysis and cited market benchmarks.",
      icon: FileText,
    },
    {
      title: "Negotiation Summary",
      description: "Executive summary of the strategy and outcome.",
      icon: FileSignature,
    },
    {
      title: "Settlement Agreement",
      description: "Signed agreement documenting the final terms.",
      icon: Handshake,
    },
    {
      title: "Final Invoice",
      description: "Vendor's revised invoice reflecting the settled amount.",
      icon: Receipt,
    },
    {
      title: "Vendor Correspondence",
      description: "Full archive of every vendor communication.",
      icon: Mail,
    },
  ];

  const journey = [
    { label: "Invoice Received", icon: Receipt },
    { label: "Professional AI Review", icon: Sparkles },
    { label: "Professional Findings", icon: FileText },
    { label: "Negotiation Started", icon: FileSignature },
    { label: "Vendor Response", icon: Mail },
    { label: "Settlement", icon: Handshake },
    { label: "Completed", icon: CheckCircle2 },
  ];

  return (
    <div className="space-y-8">
      {/* Celebration hero */}
      <section className="relative overflow-hidden rounded-3xl border border-savings/30 bg-gradient-to-br from-white via-savings/[0.04] to-purple-50/50 p-8 shadow-card md:p-12 animate-fade-in">
        <div className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-savings/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-16 h-72 w-72 rounded-full bg-purple-300/20 blur-3xl" />

        <div className="relative flex flex-col items-center text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-savings/30 bg-white/80 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.24em] text-savings shadow-sm">
            <BadgeCheck className="h-3.5 w-3.5" />
            Negotiation Complete
          </div>
          <div className="mt-6 flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-savings to-emerald-600 text-white shadow-lg animate-scale-in">
            <CheckCircle2 className="h-10 w-10" />
          </div>
          <h1 className="mt-6 font-display text-4xl font-semibold text-navy md:text-5xl">
            Congratulations.
          </h1>
          <p className="mt-3 max-w-xl text-base leading-relaxed text-muted-foreground">
            Your negotiation has successfully concluded. Here is the full record of
            what your team recovered on your behalf.
          </p>

          {/* Hero savings card */}
          <div className="mt-10 w-full max-w-2xl rounded-3xl border border-savings/30 bg-white/90 p-8 shadow-lg backdrop-blur">
            <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-savings">
              Total Saved
            </div>
            <div className="mt-2 font-display text-6xl font-semibold tracking-tight text-savings md:text-7xl">
              {fmt(totalSaved)}
            </div>
            <div className="mt-1 text-sm text-muted-foreground">
              {percentSaved}% recovered from the original invoice
            </div>

            <div className="mt-8 grid grid-cols-2 gap-3 text-left sm:grid-cols-4">
              <div className="rounded-xl border border-border bg-white p-4">
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Original Invoice
                </div>
                <div className="mt-1 font-display text-lg font-semibold text-navy">
                  {fmt(original)}
                </div>
              </div>
              <div className="rounded-xl border border-border bg-white p-4">
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Final Settlement
                </div>
                <div className="mt-1 font-display text-lg font-semibold text-navy">
                  {fmt(settlement)}
                </div>
              </div>
              <div className="rounded-xl border border-savings/30 bg-savings/5 p-4">
                <div className="text-[10px] font-bold uppercase tracking-wider text-savings">
                  % Saved
                </div>
                <div className="mt-1 font-display text-lg font-semibold text-savings">
                  {percentSaved}%
                </div>
              </div>
              <div className="rounded-xl border border-savings/30 bg-savings/5 p-4">
                <div className="text-[10px] font-bold uppercase tracking-wider text-savings">
                  Amount Reduced
                </div>
                <div className="mt-1 font-display text-lg font-semibold text-savings">
                  {fmt(totalSaved)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Case results */}
      <section className="rounded-2xl border border-border bg-white p-6 shadow-card md:p-8">
        <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-purple-700">
          Case Results
        </div>
        <h2 className="mt-1 font-display text-2xl text-navy">Engagement summary.</h2>

        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: "Duration", value: days ? `${days} day${days === 1 ? "" : "s"}` : "—", icon: Clock },
            { label: "Specialist", value: specialist, icon: UserCircle2 },
            { label: "Vendor", value: vendor, icon: Building2 },
            { label: "Settlement Date", value: dateLabel(settlementDate), icon: Calendar },
            { label: "Total Communications", value: String(totalComms), icon: MessageSquare },
            { label: "Documents Generated", value: String(docsGenerated), icon: FileText },
            { label: "Letters Sent", value: String(lettersSent), icon: FileSignature },
            { label: "Case Status", value: "Closed · Archived", icon: BadgeCheck },
          ].map((m) => (
            <div key={m.label} className="rounded-xl border border-border bg-secondary/30 p-4">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <m.icon className="h-3 w-3 text-purple-700" />
                {m.label}
              </div>
              <div className="mt-1 font-display text-sm font-semibold text-navy">
                {m.value}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Success timeline — all green */}
      <section className="rounded-2xl border border-border bg-white p-6 shadow-card md:p-8">
        <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-savings">
          Successful Journey
        </div>
        <h2 className="mt-1 font-display text-2xl text-navy">Case journey.</h2>

        <div className="mt-6 flex flex-col gap-3 md:flex-row md:items-stretch md:gap-2">
          {journey.map((step, i) => {
            const Icon = step.icon;
            return (
              <div key={step.label} className="flex items-center gap-2 md:flex-1 md:flex-col md:text-center">
                <div className="flex items-center gap-2 md:flex-col md:gap-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-savings to-emerald-600 text-white shadow-sm">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="text-xs font-semibold text-navy md:mt-1">{step.label}</div>
                </div>
                {i < journey.length - 1 && (
                  <div className="hidden h-0.5 flex-1 bg-gradient-to-r from-savings/60 to-savings/20 md:block" />
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Final package */}
      <section className="rounded-2xl border border-border bg-white p-6 shadow-card md:p-8">
        <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-purple-700">
          Final Case Package
        </div>
        <h2 className="mt-1 font-display text-2xl text-navy">Your complete record.</h2>
        <p className="mt-1 max-w-lg text-sm text-muted-foreground">
          A downloadable archive of every document produced during your negotiation.
        </p>

        <div className="mt-6 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {packageDocs.map((doc) => (
            <div
              key={doc.title}
              className="rounded-2xl border border-border bg-gradient-to-br from-white to-purple-50/30 p-5 transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-100 text-purple-700">
                <doc.icon className="h-5 w-5" />
              </div>
              <div className="mt-3 font-semibold text-navy">{doc.title}</div>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                {doc.description}
              </p>
              <div className="mt-4 flex flex-wrap gap-1.5 border-t border-border/60 pt-3">
                <button className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-3 py-1.5 text-[11px] font-semibold text-navy hover:border-purple-300 hover:text-purple-700">
                  <Eye className="h-3 w-3" />
                  View
                </button>
                <button className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-3 py-1.5 text-[11px] font-semibold text-navy hover:border-purple-300 hover:text-purple-700">
                  <Download className="h-3 w-3" />
                  Download
                </button>
                <button className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-3 py-1.5 text-[11px] font-semibold text-navy hover:border-purple-300 hover:text-purple-700">
                  <Printer className="h-3 w-3" />
                  Print
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Thank you message */}
      <section className="rounded-2xl border border-purple-200 bg-gradient-to-br from-purple-50/60 via-white to-white p-6 shadow-card md:p-8">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-500 via-purple-600 to-purple-800 font-display text-xl font-semibold text-white shadow-lg">
            SM
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-purple-700">
              A note from your specialist
            </div>
            <p className="mt-2 font-display text-xl leading-relaxed text-navy">
              &ldquo;It was our pleasure helping recover savings on your behalf.
              Thank you for trusting ClaimGuard.&rdquo;
            </p>
            <div className="mt-3 text-sm font-semibold text-navy">{specialist}</div>
            <div className="text-xs text-muted-foreground">
              Senior Invoice Negotiation Specialist
            </div>
          </div>
        </div>
      </section>

      {/* Next steps */}
      <section className="rounded-2xl border border-border bg-white p-6 shadow-card md:p-8">
        <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-purple-700">
          Next Steps
        </div>
        <h2 className="mt-1 font-display text-2xl text-navy">Where would you like to go?</h2>

        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { title: "Submit Another Invoice", body: "Start a new review in minutes.", to: "/submit", icon: Receipt, primary: true },
            { title: "Return to Dashboard", body: "See all your reviews and cases.", to: "/dashboard", icon: BarChart3 },
            { title: "Download Case File", body: "Grab a full archive of this case.", icon: Download },
            { title: "Contact Your Specialist", body: "Message Sarah with follow-ups.", scrollTo: "negotiation-messages", icon: MessageSquare },
          ].map((c) => {
            const inner = (
              <div
                className={`h-full rounded-2xl border p-5 text-left transition hover:-translate-y-0.5 hover:shadow-md ${
                  c.primary
                    ? "border-purple-300 bg-gradient-to-br from-purple-50 to-white"
                    : "border-border bg-white"
                }`}
              >
                <div
                  className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                    c.primary ? "bg-purple-600 text-white" : "bg-purple-100 text-purple-700"
                  }`}
                >
                  <c.icon className="h-5 w-5" />
                </div>
                <div className="mt-3 font-semibold text-navy">{c.title}</div>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  {c.body}
                </p>
                <div className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-purple-700">
                  Continue <ArrowRight className="h-3 w-3" />
                </div>
              </div>
            );
            if (c.to) {
              return (
                <Link key={c.title} to={c.to} className="block">
                  {inner}
                </Link>
              );
            }
            if (c.scrollTo) {
              return (
                <button
                  key={c.title}
                  type="button"
                  onClick={() => {
                    document.getElementById(c.scrollTo!)?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className="text-left"
                >
                  {inner}
                </button>
              );
            }
            return (
              <button key={c.title} type="button" className="text-left">
                {inner}
              </button>
            );
          })}
        </div>
      </section>

      {/* Future features */}
      <section className="rounded-2xl border border-dashed border-border bg-secondary/30 p-6 md:p-8">
        <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-foreground">
          Coming Soon
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {[
            { title: "Refer a Friend", body: "Share the savings with someone you know.", icon: Users },
            { title: "Leave a Review", body: "Tell us how we did — help others find us.", icon: HeartPulse },
            { title: "Enterprise Plans", body: "Volume negotiation for teams and finance ops.", icon: Landmark },
          ].map((c) => (
            <div key={c.title} className="rounded-xl border border-border bg-white/70 p-4">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-100 text-purple-700">
                  <c.icon className="h-4 w-4" />
                </div>
                <div className="font-semibold text-navy">{c.title}</div>
                <span className="ml-auto rounded-full bg-secondary px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                  Soon
                </span>
              </div>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                {c.body}
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Premium Savings Tracker (below hero)                        */
/* ─────────────────────────────────────────────────────────── */

function SavingsTracker({
  review,
  negotiation,
  canonicalRecovery,
}: {
  review: Review;
  negotiation: Negotiation | null;
  canonicalRecovery: number | null;
}) {
  const original = Number(negotiation?.original_amount ?? review.total_amount ?? 0);
  const currentOffer = negotiation?.final_offer ?? negotiation?.counter_offer ?? null;
  const target = negotiation?.estimated_target ?? null;
  const settlement = negotiation?.settlement_amount ?? null;

  const financial = resolveNegotiationFinancial({
    originalAmount: original || null,
    settlementAmount: settlement,
    canonicalRecovery,
  });
  const outcomeLabel = financial.label;
  const outcomeValue = financial.value;

  const progressPercent =
    currentOffer != null && target != null && original && original > target
      ? Math.min(100, Math.max(0, Math.round(((original - Number(currentOffer)) / (original - Number(target))) * 100)))
      : null;

  const steps = [
    {
      key: "original",
      label: "Original Invoice",
      icon: <Receipt className="h-5 w-5" />,
      value: original ? `$${original.toLocaleString()}` : "Pending review",
      pending: !original,
      accent: "navy" as const,
    },
    {
      key: "offer",
      label: "Current Vendor Offer",
      icon: <Landmark className="h-5 w-5" />,
      value:
        currentOffer != null
          ? `$${Math.round(Number(currentOffer)).toLocaleString()}`
          : "Waiting for vendor response",
      pending: currentOffer == null,
      accent: "trust" as const,
    },
    {
      key: "target",
      label: "Target Settlement",
      icon: <Target className="h-5 w-5" />,
      value: target ? `$${Number(target).toLocaleString()}` : "Target being prepared",
      pending: target == null,
      accent: "purple" as const,
    },
    {
      key: "savings",
      label: outcomeLabel,
      icon: <TrendingUp className="h-5 w-5" />,
      value: outcomeValue,
      pending: financial.kind === "unavailable",
      accent: "savings" as const,
    },
  ];

  const badgeClass =
    "inline-flex items-center gap-1.5 rounded-full border bg-white px-3 py-1 text-[11px] font-medium text-muted-foreground shadow-sm";

  return (
    <section className="report-rise">
      <div className="rounded-3xl border border-border bg-white p-6 shadow-card md:p-8">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-foreground">
              Savings Tracker
            </div>
            <h2 className="mt-1 font-display text-2xl text-navy">
              Track how your negotiation is moving toward a lower final balance.
            </h2>
          </div>
        </div>

        {/* Desktop: horizontal tracker */}
        <div className="mt-8 hidden md:block">
          <div className="flex items-stretch gap-3">
            {steps.map((step, idx) => {
              const isLast = idx === steps.length - 1;
              const accentBg =
                step.accent === "savings"
                  ? "bg-emerald-50 border-emerald-200"
                  : step.accent === "purple"
                    ? "bg-purple-50/60 border-purple-200"
                    : step.accent === "trust"
                      ? "bg-sky-50/60 border-sky-200"
                      : "bg-secondary/50 border-border";
              const accentIcon =
                step.accent === "savings"
                  ? "text-emerald-700 bg-emerald-100"
                  : step.accent === "purple"
                    ? "text-purple-700 bg-purple-100"
                    : step.accent === "trust"
                      ? "text-sky-700 bg-sky-100"
                      : "text-navy bg-navy/10";
              const accentText =
                step.accent === "savings"
                  ? "text-emerald-700"
                  : step.accent === "purple"
                    ? "text-purple-700"
                    : step.accent === "trust"
                      ? "text-sky-700"
                      : "text-navy";

              return (
                <div key={step.key} className="flex flex-1 items-stretch gap-3">
                  <div
                    className={`flex flex-1 flex-col rounded-2xl border p-5 transition hover:-translate-y-0.5 hover:shadow-card ${accentBg}`}
                  >
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-xl ${accentIcon}`}
                    >
                      {step.icon}
                    </div>
                    <div className="mt-3 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                      {step.label}
                    </div>
                    <div className={`mt-1 font-display text-xl ${accentText}`}>
                      {step.value}
                    </div>
                    {step.pending && (
                      <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        Pending
                      </div>
                    )}
                  </div>
                  {!isLast && (
                    <div className="flex items-center">
                      <ArrowRight className="h-5 w-5 text-muted-foreground/40" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile: vertical stacked */}
        <div className="mt-6 block md:hidden space-y-3">
          {steps.map((step, idx) => {
            const accentBg =
              step.accent === "savings"
                ? "bg-emerald-50 border-emerald-200"
                : step.accent === "purple"
                  ? "bg-purple-50/60 border-purple-200"
                  : step.accent === "trust"
                    ? "bg-sky-50/60 border-sky-200"
                    : "bg-secondary/50 border-border";
            const accentIcon =
              step.accent === "savings"
                ? "text-emerald-700 bg-emerald-100"
                : step.accent === "purple"
                  ? "text-purple-700 bg-purple-100"
                  : step.accent === "trust"
                    ? "text-sky-700 bg-sky-100"
                    : "text-navy bg-navy/10";
            const accentText =
              step.accent === "savings"
                ? "text-emerald-700"
                : step.accent === "purple"
                  ? "text-purple-700"
                  : step.accent === "trust"
                    ? "text-sky-700"
                    : "text-navy";

            return (
              <div key={step.key}>
                <div
                  className={`flex items-center gap-4 rounded-2xl border p-4 ${accentBg}`}
                >
                  <div
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${accentIcon}`}
                  >
                    {step.icon}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                      {step.label}
                    </div>
                    <div className={`mt-0.5 font-display text-lg ${accentText}`}>
                      {step.value}
                    </div>
                  </div>
                </div>
                {idx < steps.length - 1 && (
                  <div className="flex justify-center py-1">
                    <ArrowDown className="h-4 w-4 text-muted-foreground/30" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Progress toward target */}
        <div className="mt-8 rounded-2xl border border-border bg-secondary/30 p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-navy">
              <Percent className="h-4 w-4 text-purple-700" />
              Negotiation Progress Toward Target
            </div>
            {progressPercent != null ? (
              <span className="font-display text-lg text-purple-700">{progressPercent}%</span>
            ) : (
              <span className="text-sm font-medium text-muted-foreground">Preparing first outreach</span>
            )}
          </div>
          {progressPercent != null && (
            <>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-white">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-purple-500 to-purple-700 transition-all duration-700"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                {progressPercent}% toward target settlement
              </p>
            </>
          )}
        </div>

        {/* Explanation */}
        <div className="mt-5 flex items-start gap-3 rounded-xl border border-border bg-white p-4">
          <BarChart3 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {financial.kind === "actual_settlement"
              ? "Actual Negotiated Savings is the difference between your original invoice and the final settlement recorded by your specialist."
              : financial.kind === "supported_recovery"
                ? "Total Supported Recovery comes directly from your Professional Findings Report — the same figure shown on your dashboard and in the report."
                : "A financial opportunity has not been established for this claim yet. It will appear here once your Professional Findings Report is complete."}
          </p>
        </div>

        {/* Trust badges */}
        <div className="mt-5 flex flex-wrap gap-2">
          <span className={badgeClass}>
            <BadgeCheck className="h-3.5 w-3.5 text-savings" />
            Based on findings report
          </span>
          <span className={badgeClass}>
            <TrendingUp className="h-3.5 w-3.5 text-trust" />
            Market-backed estimate
          </span>
          <span className={badgeClass}>
            <UserCircle2 className="h-3.5 w-3.5 text-purple-700" />
            Specialist reviewed
          </span>
          <span className={badgeClass}>
            <Activity className="h-3.5 w-3.5 text-fasttrack" />
            Updated as offers change
          </span>
        </div>
      </div>
    </section>
  );
}

function SpecialistAndNextUpdate({
  negotiation,
  review,
}: {
  negotiation: Negotiation | null;
  review: Review;
}) {
  const assigned = !!negotiation?.assigned_specialist_id;
  const status = negotiation?.status ?? review.status;
  const nextAction = computeCustomerNextAction(status, assigned);
  const initials = assigned ? "SN" : "··";
  const updatedAt = negotiation?.updated_at ?? negotiation?.created_at ?? review.updated_at;

  const profile = specialistProfile(assigned);
  const presence = specialistPresence(status, assigned);
  const focus = specialistFocus(status, assigned);

  return (
    <section className="grid gap-4 lg:grid-cols-3">
      {/* Premium specialist profile — spans 2 cols on desktop */}
      <div className="lg:col-span-2 rounded-2xl border border-purple-100 bg-gradient-to-br from-white via-white to-purple-50/40 p-6 sm:p-8 shadow-card animate-fade-in">
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-purple-700">
            Your dedicated specialist
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-purple-200 bg-white/70 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-purple-700">
            <BadgeCheck className="h-3 w-3" />
            Verified negotiator
          </span>
        </div>

        <div className="mt-5 flex flex-col gap-5 sm:flex-row sm:items-start">
          <div
            className={`relative flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl font-display text-2xl font-semibold shadow-lg ${
              assigned
                ? "bg-gradient-to-br from-purple-500 via-purple-600 to-purple-800 text-white"
                : "bg-secondary text-muted-foreground"
            }`}
          >
            {assigned ? profile.initials : <UserCircle2 className="h-10 w-10" />}
            <span
              className={`absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white ${presence.dotBg}`}
              aria-hidden
            >
              <span className={`h-2 w-2 rounded-full bg-white ${assigned ? "animate-pulse" : ""}`} />
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display text-2xl font-semibold text-navy">
              {assigned ? profile.name : "Assigning your specialist"}
            </div>
            <div className="mt-0.5 text-sm text-muted-foreground">{profile.title}</div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${presence.chip}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${presence.dotBg}`} />
                {presence.label}
              </span>
              <span className="text-[11px] text-muted-foreground">{presence.note}</span>
            </div>
            {assigned && (
              <p className="mt-4 rounded-xl border border-purple-100 bg-white/70 p-4 text-sm leading-relaxed text-navy/90">
                <span className="font-semibold">Hi, I'm {profile.firstName}.</span>{" "}
                I'll personally oversee your negotiation, coordinate every vendor
                communication, and keep you informed throughout the process.
              </p>
            )}
          </div>
        </div>

        {/* Credibility metrics */}
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { icon: Handshake, label: "Negotiations", value: "487" },
            { icon: Percent, label: "Avg. savings", value: "24%" },
            { icon: Clock, label: "Response", value: "< 1 day" },
            { icon: HeartPulse, label: "Satisfaction", value: "4.9 / 5" },
          ].map((m) => (
            <div key={m.label} className="rounded-xl border border-border bg-white/80 p-3">
              <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                <m.icon className="h-3 w-3 text-purple-700" />
                {m.label}
              </div>
              <div className="mt-1 font-display text-lg font-semibold text-navy">
                {m.value}
              </div>
            </div>
          ))}
        </div>

        {/* Contact options */}
        <div className="mt-6 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              const el = document.getElementById("negotiation-messages");
              el?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="inline-flex items-center gap-2 rounded-full bg-navy px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-navy/90"
          >
            <MessageSquare className="h-3.5 w-3.5" />
            Message specialist
          </button>
          <button
            type="button"
            disabled
            className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-xs font-semibold text-muted-foreground"
          >
            <PhoneCall className="h-3.5 w-3.5" />
            Schedule callback
            <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
              Soon
            </span>
          </button>
          <span className="inline-flex items-center gap-2 rounded-full border border-savings/30 bg-savings/5 px-4 py-2 text-xs font-semibold text-savings">
            <Mail className="h-3.5 w-3.5" />
            Email notifications on
          </span>
        </div>

        {/* Currently working on + workload */}
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-purple-100 bg-white p-4">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-purple-700">
              <Target className="h-3 w-3" />
              Currently working on
            </div>
            <div className="mt-1.5 font-semibold text-navy">{focus.title}</div>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {focus.detail}
            </p>
          </div>
          <div className="rounded-xl border border-border bg-white p-4">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              <Gauge className="h-3 w-3 text-purple-700" />
              Case workload
            </div>
            <div className="mt-1.5 font-semibold text-navy">Actively managing your case</div>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              Expect an average update every 1–2 business days.
            </p>
          </div>
        </div>

        {updatedAt && (
          <div className="mt-5 flex items-center gap-1.5 border-t border-border/60 pt-4 text-[11px] text-muted-foreground">
            <Clock className="h-3 w-3" />
            Last activity{" "}
            {new Date(updatedAt).toLocaleString(undefined, {
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
            })}
          </div>
        )}
      </div>

      {/* Expected next update card — unchanged */}
      <div className="rounded-2xl border border-purple-200 bg-gradient-to-br from-white to-purple-50/50 p-6 shadow-card">
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-purple-700">
            Expected next update
          </div>
          <Sparkles className="h-4 w-4 text-purple-700" />
        </div>
        <div className="mt-3 font-display text-lg text-navy">{nextAction.title}</div>
        <p className="mt-1.5 text-sm text-muted-foreground">{nextAction.body}</p>
        <div className="mt-4 grid grid-cols-2 gap-3 text-xs lg:grid-cols-1">
          <div className="rounded-lg border border-border bg-white/80 p-3">
            <div className="font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">
              Estimated time
            </div>
            <div className="mt-1 font-semibold text-navy">{nextAction.eta}</div>
          </div>
          <div className="rounded-lg border border-border bg-white/80 p-3">
            <div className="font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">
              Responsible
            </div>
            <div className="mt-1 font-semibold text-navy">{nextAction.owner}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function specialistProfile(assigned: boolean) {
  if (!assigned) {
    return {
      initials: "··",
      name: "Being assigned",
      firstName: "your specialist",
      title: "Senior Invoice Negotiation Specialist",
    };
  }
  return {
    initials: "SM",
    name: "Sarah Morgan",
    firstName: "Sarah",
    title: "Senior Invoice Negotiation Specialist",
  };
}

function specialistPresence(status: string, assigned: boolean) {
  if (!assigned) {
    return {
      label: "Assigning",
      note: "Matching you with the right negotiator.",
      chip: "bg-secondary text-muted-foreground",
      dotBg: "bg-muted-foreground",
    };
  }
  if (status === "assigned" || status === "preparing") {
    return {
      label: "Reviewing",
      note: "Reading your findings report and evidence.",
      chip: "bg-purple-100 text-purple-700",
      dotBg: "bg-purple-500",
    };
  }
  if (
    status === "waiting_response" ||
    status === "waiting_vendor" ||
    status === "waiting_on_vendor"
  ) {
    return {
      label: "Awaiting vendor",
      note: "Following up if we don't hear back in 3 business days.",
      chip: "bg-amber-100 text-amber-800",
      dotBg: "bg-amber-500",
    };
  }
  if (
    status === "vendor_contacted" ||
    status === "negotiating" ||
    status === "in_progress" ||
    status === "offer_received"
  ) {
    return {
      label: "Negotiating",
      note: "Currently preparing the next vendor communication.",
      chip: "bg-purple-100 text-purple-700",
      dotBg: "bg-purple-500",
    };
  }
  if (status === "accepted" || status === "settled" || status === "completed") {
    return {
      label: "Available",
      note: "Case complete — reach out anytime with questions.",
      chip: "bg-savings/10 text-savings",
      dotBg: "bg-savings",
    };
  }
  return {
    label: "Available",
    note: "Actively monitoring your case.",
    chip: "bg-savings/10 text-savings",
    dotBg: "bg-savings",
  };
}

function specialistFocus(status: string, assigned: boolean) {
  if (!assigned) {
    return {
      title: "Case intake",
      detail: "Assigning the right negotiator based on vendor type and complexity.",
    };
  }
  if (status === "assigned" || status === "preparing") {
    return {
      title: "Evidence review",
      detail: "Reading the findings report and preparing the initial vendor demand letter.",
    };
  }
  if (
    status === "waiting_response" ||
    status === "waiting_vendor" ||
    status === "waiting_on_vendor"
  ) {
    return {
      title: "Waiting on documentation",
      detail: "Vendor has been contacted — we'll follow up on their response promptly.",
    };
  }
  if (status === "vendor_contacted") {
    return {
      title: "Vendor follow-up",
      detail: "Coordinating replies and confirming receipt of our demand letter.",
    };
  }
  if (status === "negotiating" || status === "in_progress" || status === "offer_received") {
    return {
      title: "Settlement preparation",
      detail: "Exchanging counter-offers to secure the best settlement for you.",
    };
  }
  if (status === "accepted" || status === "settled" || status === "completed") {
    return {
      title: "Case closed",
      detail: "Final paperwork and savings summary are available in your documents.",
    };
  }
  return {
    title: "Active case management",
    detail: "Monitoring the file and posting updates as soon as they happen.",
  };
}

function computeCustomerNextAction(
  status: string,
  assigned: boolean,
): { title: string; body: string; eta: string; owner: string } {
  if (!assigned || status === "expert_negotiation_requested" || status === "requested") {
    return {
      title: "Assigning your specialist",
      body: "We're matching your case to the right negotiator based on vendor type and dispute complexity.",
      eta: "Within 1 business hour",
      owner: "Case operations",
    };
  }
  if (status === "assigned" || status === "preparing") {
    return {
      title: "Building your evidence packet",
      body: "Your specialist is reading the findings report and preparing the initial vendor outreach.",
      eta: "1–2 business days",
      owner: "Your specialist",
    };
  }
  if (status === "vendor_contacted" || status === "waiting_response" || status === "waiting_vendor" || status === "waiting_on_vendor") {
    return {
      title: "Awaiting the vendor's response",
      body: "We've sent the formal negotiation letter. We follow up if we don't hear back within 3 business days.",
      eta: "3–5 business days",
      owner: "Vendor",
    };
  }
  if (status === "negotiating" || status === "in_progress") {
    return {
      title: "Active negotiation underway",
      body: "Your specialist is exchanging counter-offers with the vendor to reach the best settlement.",
      eta: "5–10 business days",
      owner: "Your specialist",
    };
  }
  if (status === "offer_received") {
    return {
      title: "Offer ready for your review",
      body: "The vendor has responded with an offer. We'll email you a summary and recommendation shortly.",
      eta: "Within 1 business day",
      owner: "You & your specialist",
    };
  }
  if (status === "accepted" || status === "settled" || status === "completed") {
    return {
      title: "Settlement finalized",
      body: "Your case is closed. Final paperwork and savings summary are in the documents section below.",
      eta: "Complete",
      owner: "—",
    };
  }
  return {
    title: "Your specialist is working the case",
    body: "We'll post the next meaningful update to your activity feed as soon as it happens.",
    eta: "Ongoing",
    owner: "Your specialist",
  };
}



function NegotiationStatusPanel({
  steps,
  negotiation,
  review,
}: {
  steps: ReturnType<typeof negotiationStages>;
  negotiation: Negotiation | null;
  review: import("@/lib/db/models").Review;
}) {
  const latestUpdate = negotiation?.updated_at ?? negotiation?.created_at;
  const original = negotiation?.original_amount ?? review.total_amount ?? 0;
  const currentOffer =
    negotiation?.final_offer ?? negotiation?.counter_offer ?? negotiation?.estimated_target;
  const savings =
    negotiation?.settlement_amount != null && original
      ? Number(original) - Number(negotiation.settlement_amount)
      : currentOffer && original
        ? Number(original) - Number(currentOffer)
        : null;
  const percent = Math.round(
    ((steps.findIndex((s) => s.current) + 1) / steps.length) * 100,
  );

  const stat = (label: string, value: React.ReactNode, icon: React.ReactNode) => (
    <div className="rounded-xl border border-border bg-secondary/40 p-4">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="mt-1.5 text-sm font-semibold text-navy">{value}</div>
    </div>
  );

  return (
    <section className="rounded-2xl border border-border bg-white p-6 shadow-card md:p-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-foreground">
            Negotiation status
          </div>
          <h2 className="mt-1 font-display text-2xl text-navy">
            {negotiation?.negotiation_number ?? "Negotiation"} · Progress
          </h2>
        </div>
        {latestUpdate && (
          <div className="text-right">
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Last activity
            </div>
            <div className="mt-0.5 text-sm text-navy">
              {new Date(latestUpdate).toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}
            </div>
          </div>
        )}
      </div>

      {/* Facts */}
      <div className="mt-6 grid gap-3 md:grid-cols-4">
        {stat(
          "Negotiation ID",
          negotiation?.negotiation_number ?? "—",
          <Hash className="h-3 w-3" />,
        )}
        {stat(
          "Specialist",
          negotiation?.assigned_specialist_id ? "Assigned" : "Being assigned",
          <UserCircle2 className="h-3 w-3" />,
        )}
        {stat(
          "Current status",
          prettyStatus(negotiation?.status ?? review.status),
          <Activity className="h-3 w-3" />,
        )}
        {stat(
          "Total Supported Recovery",
          savings != null && savings > 0
            ? `$${Math.round(Number(savings)).toLocaleString()}`
            : "Calculating…",
          <TrendingUp className="h-3 w-3 text-savings" />,
        )}
        {stat(
          "Current offer",
          currentOffer != null
            ? `$${Math.round(Number(currentOffer)).toLocaleString()}`
            : "Awaiting vendor",
          <DollarSign className="h-3 w-3" />,
        )}
        {stat(
          "Vendor response",
          negotiation?.vendor_response ? "Received" : "Pending",
          <Mail className="h-3 w-3" />,
        )}
        {stat(
          "Started",
          negotiation?.created_at
            ? new Date(negotiation.created_at).toLocaleDateString()
            : "—",
          <Calendar className="h-3 w-3" />,
        )}
        {stat(
          "Next step",
          nextStepLabel(steps),
          <ArrowRight className="h-3 w-3" />,
        )}
      </div>

      {/* Progress bar */}
      <div className="mt-8">
        <div className="flex items-center justify-between text-xs font-semibold text-muted-foreground">
          <span>Case progress</span>
          <span className="text-purple-700">{percent}%</span>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-secondary">
          <div
            className="h-full rounded-full bg-gradient-to-r from-purple-500 to-purple-700 transition-all duration-700"
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>

      {/* Timeline stages */}
      <ol className="mt-8 grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-9">
        {steps.map((s, i) => (
          <li key={s.key} className="relative">
            <div
              className={`rounded-2xl border p-4 transition-all duration-500 ${
                s.current
                  ? "border-purple-600 bg-purple-50/70 shadow-[0_8px_24px_-12px_rgba(147,51,234,0.45)] ring-2 ring-purple-200"
                  : s.reached
                    ? "border-purple-200 bg-white"
                    : "border-dashed border-border bg-secondary/30"
              }`}
            >
              <div
                className={`flex h-9 w-9 items-center justify-center rounded-full border-2 transition-colors ${
                  s.reached
                    ? "border-purple-600 bg-purple-600 text-white"
                    : "border-border bg-white text-muted-foreground"
                } ${s.current ? "shadow-[0_0_0_6px_rgba(147,51,234,0.15)]" : ""}`}
              >
                {s.current ? (
                  <span className="relative flex h-4 w-4 items-center justify-center">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/70 opacity-75" />
                    <CircleDot className="relative h-4 w-4" />
                  </span>
                ) : s.reached ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : (
                  <span className="text-[11px] font-semibold">{i + 1}</span>
                )}
              </div>
              <div
                className={`mt-3 text-sm font-semibold leading-tight ${
                  s.current
                    ? "text-purple-700"
                    : s.reached
                      ? "text-navy"
                      : "text-muted-foreground"
                }`}
              >
                {s.label}
              </div>
              {s.current && (
                <div className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-purple-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
                  In progress
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function nextStepLabel(steps: ReturnType<typeof negotiationStages>): string {
  const idx = steps.findIndex((s) => s.current);
  const next = steps[idx + 1];
  return next ? next.label : "Case closing";
}

/* ─────────────────────────────────────────────────────────── */
/* Activity feed                                                */
/* ─────────────────────────────────────────────────────────── */

/* ─────────────────────────────────────────────────────────── */
/* Premium legal-style case timeline                            */
/* ─────────────────────────────────────────────────────────── */

type TimelineStageKey =
  | "review_complete"
  | "assigned"
  | "initial_contact"
  | "evidence_submitted"
  | "awaiting_response"
  | "vendor_response"
  | "counter_offer"
  | "internal_review"
  | "settlement_proposed"
  | "settlement_accepted"
  | "case_closed";

interface TimelineStageDef {
  key: TimelineStageKey;
  label: string;
  icon: React.ReactNode;
  description: string;
  match: RegExp;
}

const CASE_STAGES: TimelineStageDef[] = [
  {
    key: "review_complete",
    label: "Review Complete",
    icon: <FileText className="h-4 w-4" />,
    description:
      "Your Professional Findings Report was finalized and cleared for negotiation.",
    match: /review_complete|report_ready|findings|negotiation_requested/,
  },
  {
    key: "assigned",
    label: "Case Assigned",
    icon: <UserCircle2 className="h-4 w-4" />,
    description:
      "A senior negotiation specialist has taken ownership of your case and reviewed the file.",
    match: /assigned|preparing/,
  },
  {
    key: "initial_contact",
    label: "Initial Vendor Contact",
    icon: <Mail className="h-4 w-4" />,
    description:
      "First formal outreach was sent to the vendor requesting supporting documentation.",
    match: /vendor_contact|initial_contact|contacted|outreach/,
  },
  {
    key: "evidence_submitted",
    label: "Evidence Submitted",
    icon: <FileSignature className="h-4 w-4" />,
    description:
      "Our specialists submitted findings and supporting evidence to the vendor.",
    match: /evidence|documentation_sent/,
  },
  {
    key: "awaiting_response",
    label: "Awaiting Vendor Response",
    icon: <Clock className="h-4 w-4" />,
    description:
      "The vendor is reviewing our position. We typically hear back within 3–5 business days.",
    match: /awaiting|waiting_response|waiting_vendor|waiting_on_vendor/,
  },
  {
    key: "vendor_response",
    label: "Vendor Response Received",
    icon: <MessageSquare className="h-4 w-4" />,
    description:
      "The vendor has responded with their position. We are reviewing their reasoning.",
    match: /vendor_response|response_received/,
  },
  {
    key: "counter_offer",
    label: "Counter Offer",
    icon: <Handshake className="h-4 w-4" />,
    description:
      "A counter offer was prepared using your findings report and delivered to the vendor.",
    match: /counter|offer_received|negotiating/,
  },
  {
    key: "internal_review",
    label: "Internal Review",
    icon: <ShieldCheck className="h-4 w-4" />,
    description:
      "Our specialists are validating the terms with senior counsel before recommending action.",
    match: /internal_review|review_internal/,
  },
  {
    key: "settlement_proposed",
    label: "Settlement Proposed",
    icon: <FileText className="h-4 w-4" />,
    description:
      "Final settlement terms have been proposed and are ready for your review.",
    match: /settlement_proposed|proposal/,
  },
  {
    key: "settlement_accepted",
    label: "Settlement Accepted",
    icon: <CheckCircle2 className="h-4 w-4" />,
    description:
      "Settlement approved and executed. Vendor confirmation is being finalized.",
    match: /accepted|settled|settlement_accepted/,
  },
  {
    key: "case_closed",
    label: "Case Closed",
    icon: <Award className="h-4 w-4" />,
    description:
      "Your case is complete. All documentation has been archived to your workspace.",
    match: /completed|closed|case_closed/,
  },
];

const STATUS_TO_TIMELINE: Record<string, TimelineStageKey> = {
  draft: "review_complete",
  requested: "review_complete",
  assigned: "assigned",
  preparing: "assigned",
  vendor_contacted: "initial_contact",
  waiting_response: "awaiting_response",
  waiting_vendor: "awaiting_response",
  waiting_on_vendor: "awaiting_response",
  negotiating: "counter_offer",
  in_progress: "counter_offer",
  offer_received: "counter_offer",
  accepted: "settlement_accepted",
  settled: "settlement_accepted",
  completed: "case_closed",
};

function relativeMoment(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const startOfDay = (dt: Date) =>
    new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(d)) / 86400000);
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (diffDays === 0) return `Today · ${time}`;
  if (diffDays === 1) return `Yesterday · ${time}`;
  if (diffDays > 1 && diffDays < 7)
    return `This week · ${d.toLocaleDateString(undefined, { weekday: "long" })}`;
  if (diffDays >= 7 && diffDays < 14)
    return `Last week · ${d.toLocaleDateString(undefined, { weekday: "long" })}`;
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function deriveAttachments(evt: import("@/lib/db/models").TimelineEvent): string[] {
  const haystack = `${evt.event_type} ${evt.title} ${evt.description ?? ""}`.toLowerCase();
  const chips: string[] = [];
  if (/letter|outreach|contact/.test(haystack)) chips.push("Letter Sent");
  if (/vendor.*(pdf|response|document)/.test(haystack)) chips.push("Vendor PDF");
  if (/settlement|offer|counter/.test(haystack)) chips.push("Settlement Offer");
  if (/evidence|finding|supporting/.test(haystack)) chips.push("Supporting Documentation");
  return chips;
}

function NegotiationActivityFeed({
  timeline,
  negotiation,
  review,
}: {
  timeline: import("@/lib/db/models").TimelineEvent[];
  negotiation: Negotiation | null;
  review: Review;
}) {
  const visible = timeline.filter((e) => e.visible_to_customer);

  // Map each stage to its latest matching event (if any).
  const stageEvents = new Map<TimelineStageKey, import("@/lib/db/models").TimelineEvent>();
  for (const stage of CASE_STAGES) {
    const match = visible
      .filter((e) => stage.match.test(`${e.event_type} ${e.title}`.toLowerCase()))
      .sort((a, b) => (a.created_at > b.created_at ? -1 : 1))[0];
    if (match) stageEvents.set(stage.key, match);
  }

  // Determine current stage: prefer negotiation.status, else review.status,
  // else the last stage that has a matched event.
  let currentKey: TimelineStageKey = "review_complete";
  if (negotiation?.status && STATUS_TO_TIMELINE[negotiation.status]) {
    currentKey = STATUS_TO_TIMELINE[negotiation.status];
  } else if (review.status && STATUS_TO_TIMELINE[review.status]) {
    currentKey = STATUS_TO_TIMELINE[review.status];
  } else {
    for (let i = CASE_STAGES.length - 1; i >= 0; i--) {
      if (stageEvents.has(CASE_STAGES[i].key)) {
        currentKey = CASE_STAGES[i].key;
        break;
      }
    }
  }
  if (negotiation?.accepted_at) currentKey = "case_closed";

  const currentIdx = CASE_STAGES.findIndex((s) => s.key === currentKey);
  const hasAnyActivity = visible.length > 0 || currentIdx > 0;

  return (
    <section className="rounded-3xl border border-border bg-white p-6 shadow-card md:p-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-purple-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.24em] text-purple-700">
            <Activity className="h-3.5 w-3.5" />
            Case Timeline
          </div>
          <h2 className="mt-3 font-display text-3xl leading-tight text-navy md:text-4xl">
            Every milestone in your case.
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            A running record of every action your specialist takes on your behalf — from the moment
            we take on the case through final settlement.
          </p>
        </div>
        <div className="rounded-xl border border-border bg-secondary/40 px-4 py-2 text-right">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
            Progress
          </div>
          <div className="mt-0.5 font-display text-lg text-navy">
            Stage {currentIdx + 1} of {CASE_STAGES.length}
          </div>
        </div>
      </div>

      {!hasAnyActivity ? (
        <div className="mt-8 rounded-2xl border border-dashed border-border bg-gradient-to-br from-purple-50/60 to-white p-10 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-purple-100 text-purple-700">
            <Sparkles className="h-6 w-6" />
          </div>
          <h3 className="mt-4 font-display text-xl text-navy">Preparing the first outreach</h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            We're preparing the first outreach to the vendor. You'll see every major milestone here
            as your case progresses.
          </p>
        </div>
      ) : (
        <ol className="relative mt-10 border-l-2 border-purple-100 pl-0">
          {CASE_STAGES.map((stage, i) => {
            const evt = stageEvents.get(stage.key);
            const isCompleted = i < currentIdx || !!evt || !!(i === currentIdx && evt);
            const isCurrent = i === currentIdx;
            const isFuture = i > currentIdx;
            const timestamp = evt?.created_at;
            const attachments = evt ? deriveAttachments(evt) : [];

            return (
              <li key={stage.key} className="relative ml-8 pb-8 last:pb-0">
                {/* Dot */}
                <span
                  className={[
                    "absolute -left-[41px] top-2 flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-white transition",
                    isCurrent
                      ? "bg-purple-600 shadow-[0_0_0_6px_rgba(147,51,234,0.15)] animate-pulse"
                      : isCompleted && !isFuture
                        ? "bg-savings"
                        : "bg-secondary border border-border",
                  ].join(" ")}
                >
                  {isCompleted && !isCurrent && !isFuture ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-white" />
                  ) : isCurrent ? (
                    <CircleDot className="h-3.5 w-3.5 text-white" />
                  ) : (
                    <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50" />
                  )}
                </span>

                <div
                  className={[
                    "rounded-2xl border p-5 transition-all md:p-6",
                    isCurrent
                      ? "border-purple-300 bg-gradient-to-br from-purple-50/80 to-white shadow-elevated md:scale-[1.01]"
                      : isFuture
                        ? "border-dashed border-border bg-secondary/30"
                        : "border-border bg-white hover:shadow-card",
                  ].join(" ")}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={[
                            "flex h-8 w-8 items-center justify-center rounded-lg",
                            isCurrent
                              ? "bg-purple-600 text-white"
                              : isCompleted && !isFuture
                                ? "bg-savings/10 text-savings"
                                : "bg-secondary text-muted-foreground",
                          ].join(" ")}
                        >
                          {stage.icon}
                        </span>
                        <div
                          className={[
                            "font-display",
                            isCurrent ? "text-xl text-navy" : "text-lg",
                            isFuture ? "text-muted-foreground" : "text-navy",
                          ].join(" ")}
                        >
                          {stage.label}
                        </div>
                        {isCurrent && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-purple-600 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                            <CircleDot className="h-3 w-3" />
                            Current Stage
                          </span>
                        )}
                        {isCompleted && !isCurrent && !isFuture && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-savings/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-savings">
                            <CheckCircle2 className="h-3 w-3" />
                            Completed
                          </span>
                        )}
                        {isFuture && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                            Upcoming
                          </span>
                        )}
                      </div>

                      <p
                        className={[
                          "mt-3 text-sm leading-relaxed",
                          isFuture ? "text-muted-foreground/80" : "text-muted-foreground",
                        ].join(" ")}
                      >
                        {evt?.description || stage.description}
                      </p>

                      {(evt || isCurrent) && (
                        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
                          <span className="inline-flex items-center gap-1.5">
                            <UserCircle2 className="h-3.5 w-3.5" />
                            {negotiation?.assigned_specialist_id
                              ? "Negotiation Specialist"
                              : "Specialist Team"}
                          </span>
                          {timestamp && (
                            <span className="inline-flex items-center gap-1.5">
                              <Calendar className="h-3.5 w-3.5" />
                              {relativeMoment(timestamp)}
                            </span>
                          )}
                          {isCurrent && !timestamp && (
                            <span className="inline-flex items-center gap-1.5 font-medium text-purple-700">
                              <Activity className="h-3.5 w-3.5" />
                              In progress now
                            </span>
                          )}
                        </div>
                      )}

                      {attachments.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {attachments.map((label) => (
                            <span
                              key={label}
                              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-2.5 py-1 text-[11px] font-medium text-navy shadow-sm"
                            >
                              <Paperclip className="h-3 w-3 text-purple-600" />
                              {label}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}


/* ─────────────────────────────────────────────────────────── */
/* Documents                                                    */
/* ─────────────────────────────────────────────────────────── */

type DocCategoryKey =
  | "findings"
  | "negotiation"
  | "vendor"
  | "settlement"
  | "uploads"
  | "evidence";
type DocFilterKey =
  | "all"
  | "reports"
  | "letters"
  | "vendor"
  | "settlement"
  | "uploads"
  | "evidence";
type DocStatus =
  | "current"
  | "archived"
  | "revised"
  | "signed"
  | "draft"
  | "sent"
  | "received"
  | "reviewed"
  | "approved"
  | "completed";

interface DocItem {
  id: string;
  title: string;
  description: string;
  category: DocCategoryKey;
  filter: DocFilterKey;
  createdAt: string;
  updatedAt: string;
  size: number | null;
  mime: string | null;
  version?: string;
  status: DocStatus;
  pinned?: boolean;
  synthetic?: boolean;
}

function docIcon(mime: string | null, filter: DocFilterKey) {
  if (filter === "letters") return FileSignature;
  if (filter === "vendor") return Mail;
  if (filter === "settlement") return Handshake;
  if (filter === "reports") return FileText;
  if (filter === "evidence") return BadgeCheck;
  if (mime?.includes("image")) return Paperclip;
  return FileText;
}

function formatBytes(bytes: number | null) {
  if (!bytes || bytes <= 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDocDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const STATUS_STYLES: Record<DocStatus, string> = {
  current: "bg-savings/10 text-savings border-savings/30",
  approved: "bg-savings/10 text-savings border-savings/30",
  signed: "bg-savings/10 text-savings border-savings/30",
  completed: "bg-savings/10 text-savings border-savings/30",
  draft: "bg-amber-100 text-amber-800 border-amber-200",
  sent: "bg-purple-100 text-purple-700 border-purple-200",
  received: "bg-purple-100 text-purple-700 border-purple-200",
  reviewed: "bg-trust/10 text-trust border-trust/30",
  revised: "bg-trust/10 text-trust border-trust/30",
  archived: "bg-secondary text-muted-foreground border-border",
};

function isInvoiceFile(f: ReviewFile): boolean {
  const kind = (f.kind ?? "").toLowerCase();
  const name = f.file_name.toLowerCase();
  return kind === "invoice" || name.includes("invoice");
}

function classifyFile(f: ReviewFile): {
  category: DocCategoryKey;
  filter: DocFilterKey;
} {
  const kind = (f.kind ?? "").toLowerCase();
  const name = f.file_name.toLowerCase();
  if (isInvoiceFile(f)) return { category: "findings", filter: "reports" };
  if (kind.includes("report") || name.includes("report") || name.includes("findings"))
    return { category: "findings", filter: "reports" };
  if (kind.includes("letter") || name.includes("letter") || name.includes("demand"))
    return { category: "negotiation", filter: "letters" };
  if (
    kind.includes("settlement") ||
    name.includes("settlement") ||
    name.includes("agreement")
  )
    return { category: "settlement", filter: "settlement" };
  if (kind.includes("vendor") || name.includes("vendor") || name.includes("response"))
    return { category: "vendor", filter: "vendor" };
  if (kind.includes("photo") || kind.includes("evidence") || name.includes("evidence"))
    return { category: "evidence", filter: "evidence" };
  return { category: "uploads", filter: "uploads" };
}

function NegotiationDocuments({
  files,
  negotiation,
}: {
  files: ReviewFile[];
  negotiation: Negotiation | null;
}) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<DocFilterKey>("all");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const items: DocItem[] = useMemo(() => {
    const list: DocItem[] = files.map((f) => {
      const { category, filter: flt } = classifyFile(f);
      const isInvoice = isInvoiceFile(f);
      return {
        id: f.id,
        title: isInvoice ? `Original Submitted Invoice — ${f.file_name}` : f.file_name,
        description: isInvoice
          ? "The original invoice you submitted. Always pinned first in this case file."
          : flt === "reports"
            ? "Source document referenced in the professional findings report."
            : flt === "letters"
              ? "Formal negotiation letter drafted by your specialist."
              : flt === "vendor"
                ? "Vendor correspondence captured for your case file."
                : flt === "settlement"
                  ? "Settlement paperwork ready for review."
                  : flt === "evidence"
                    ? "Supporting evidence attached to the case."
                    : "Attachment saved to your case file.",
        category,
        filter: flt,
        createdAt: f.created_at,
        updatedAt: f.created_at,
        size: f.file_size,
        mime: f.mime_type,
        status:
          flt === "letters"
            ? "sent"
            : flt === "vendor"
              ? "received"
              : flt === "settlement"
                ? "reviewed"
                : flt === "reports"
                  ? "current"
                  : "current",
        pinned: isInvoice || flt === "reports",
        // Invoices sort before other pinned docs (Claim Intelligence Package™ etc.).
        version: isInvoice ? "Appendix A · Source" : undefined,
      };
    });
    if (negotiation?.letter_body) {
      list.push({
        id: `neg-letter-${negotiation.id}`,
        title: "Current Negotiation Letter",
        description:
          "Live draft your specialist is preparing for the vendor. Updated as the strategy evolves.",
        category: "negotiation",
        filter: "letters",
        createdAt: negotiation.created_at,
        updatedAt: negotiation.updated_at ?? negotiation.created_at,
        size: negotiation.letter_body.length,
        mime: "text/plain",
        version: "v1 · Draft",
        status: "draft",
        pinned: true,
        synthetic: true,
      });
    }
    // Sort: original invoices first, then remaining pinned docs, then rest.
    const rank = (i: DocItem) => (i.version === "Appendix A · Source" ? 0 : i.pinned ? 1 : 2);
    list.sort((a, b) => rank(a) - rank(b));
    return list;
  }, [files, negotiation]);

  const filtered = items.filter((i) => {
    if (filter !== "all" && i.filter !== filter) return false;
    if (query && !i.title.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  const pinned = filtered.filter((i) => i.pinned);
  const rest = filtered.filter((i) => !i.pinned);

  const categoryMeta: Record<
    DocCategoryKey,
    { label: string; icon: typeof FileText; hint: string }
  > = {
    findings: {
      label: "Professional Findings",
      icon: FileText,
      hint: "Reports, source invoices, and analysis",
    },
    negotiation: {
      label: "Negotiation Letters",
      icon: FileSignature,
      hint: "Drafts and letters sent to the vendor",
    },
    vendor: {
      label: "Vendor Communications",
      icon: Mail,
      hint: "Responses and correspondence from the vendor",
    },
    settlement: {
      label: "Settlement Documents",
      icon: Handshake,
      hint: "Offers, agreements, and signed paperwork",
    },
    uploads: {
      label: "Customer Uploads",
      icon: Paperclip,
      hint: "Documents you contributed to the case",
    },
    evidence: {
      label: "Supporting Evidence",
      icon: BadgeCheck,
      hint: "Photos, receipts, and supplemental proof",
    },
  };

  const filterChips: Array<{ key: DocFilterKey; label: string }> = [
    { key: "all", label: "All" },
    { key: "reports", label: "Reports" },
    { key: "letters", label: "Letters" },
    { key: "vendor", label: "Vendor" },
    { key: "settlement", label: "Settlement" },
    { key: "uploads", label: "Uploads" },
    { key: "evidence", label: "Evidence" },
  ];

  const hasAny = items.length > 0;
  const lastUpdated = items.reduce<string | null>((acc, i) => {
    if (!acc || new Date(i.updatedAt) > new Date(acc)) return i.updatedAt;
    return acc;
  }, null);
  const totalBytes = items.reduce((sum, i) => sum + (i.size ?? 0), 0);
  const storageLabel = formatBytes(totalBytes);

  return (
    <section className="rounded-2xl border border-border bg-white p-6 shadow-card md:p-8">
      {/* Executive header */}
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div className="max-w-xl">
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-purple-700">
            Case Documents
          </div>
          <h2 className="mt-1 font-display text-2xl text-navy sm:text-3xl">
            Documents Center
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Every report, negotiation letter, vendor response, and settlement document
            related to your case — organized in one secure client portal.
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-savings/30 bg-savings/5 px-3 py-1.5 text-[11px] font-semibold text-savings">
          <Lock className="h-3.5 w-3.5" />
          Secure access
        </span>
      </div>

      {/* Header metrics */}
      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-border bg-gradient-to-br from-white to-purple-50/40 p-4">
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-purple-700">
            <FolderOpen className="h-3 w-3" />
            Total documents
          </div>
          <div className="mt-1 font-display text-2xl font-semibold text-navy">
            {items.length}
          </div>
        </div>
        <div className="rounded-xl border border-border bg-white p-4">
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <Clock className="h-3 w-3 text-purple-700" />
            Last updated
          </div>
          <div className="mt-1 font-display text-sm font-semibold text-navy">
            {lastUpdated ? formatDocDate(lastUpdated) : "No activity yet"}
          </div>
        </div>
        <div className="rounded-xl border border-border bg-white p-4">
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <HardDrive className="h-3 w-3 text-purple-700" />
            Storage used
          </div>
          <div className="mt-1 font-display text-sm font-semibold text-navy">
            {storageLabel ?? "—"}
          </div>
        </div>
      </div>

      {/* Search + filters */}
      <div className="mt-6 flex flex-col gap-3 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search documents..."
            className="w-full rounded-full border border-border bg-white py-2 pl-9 pr-4 text-sm text-navy placeholder:text-muted-foreground focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-200"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {filterChips.map((c) => {
            const active = filter === c.key;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => setFilter(c.key)}
                className={`rounded-full px-3 py-1.5 text-[11px] font-semibold transition ${
                  active
                    ? "bg-navy text-white shadow-sm"
                    : "border border-border bg-white text-muted-foreground hover:text-navy"
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </div>

      {!hasAny ? (
        <div className="mt-8 rounded-2xl border border-dashed border-purple-200 bg-purple-50/40 p-10 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-sm">
            <FileText className="h-5 w-5 text-purple-700" />
          </div>
          <div className="mt-3 font-display text-lg text-navy">No documents yet</div>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            Your negotiation documents will appear here as your specialist prepares
            and receives them.
          </p>
        </div>
      ) : (
        <div className="mt-6 space-y-8">
          {pinned.length > 0 && (
            <div>
              <div className="mb-3 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-purple-700">
                <Sparkles className="h-3 w-3" />
                Pinned
              </div>
              <div className="grid gap-3 lg:grid-cols-2">
                {pinned.map((doc) => (
                  <DocCard key={doc.id} doc={doc} pinned />
                ))}
              </div>
            </div>
          )}

          {(Object.keys(categoryMeta) as DocCategoryKey[]).map((catKey) => {
            const catItems = rest.filter((i) => i.category === catKey);
            if (catItems.length === 0) return null;
            const meta = categoryMeta[catKey];
            const Icon = meta.icon;
            const isCollapsed = collapsed[catKey];
            return (
              <div key={catKey} className="rounded-2xl border border-border/60 bg-secondary/20 p-4 sm:p-5">
                <button
                  type="button"
                  onClick={() =>
                    setCollapsed((prev) => ({ ...prev, [catKey]: !prev[catKey] }))
                  }
                  className="flex w-full items-center justify-between gap-3 text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white text-purple-700 shadow-sm">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 font-semibold text-navy">
                        {meta.label}
                        <span className="rounded-full bg-white px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
                          {catItems.length}
                        </span>
                      </div>
                      <div className="text-[11px] text-muted-foreground">{meta.hint}</div>
                    </div>
                  </div>
                  <ChevronDown
                    className={`h-4 w-4 text-muted-foreground transition-transform ${
                      isCollapsed ? "-rotate-90" : ""
                    }`}
                  />
                </button>
                {!isCollapsed && (
                  <div className="mt-4 grid gap-3 lg:grid-cols-2">
                    {catItems.map((doc) => (
                      <DocCard key={doc.id} doc={doc} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div className="rounded-xl border border-dashed border-border bg-secondary/30 p-6 text-center text-sm text-muted-foreground">
              No documents match your search.
            </div>
          )}
        </div>
      )}

      {/* Security panel */}
      <div className="mt-8 flex flex-col gap-3 rounded-2xl border border-border bg-gradient-to-br from-navy/[0.03] to-purple-50/40 p-4 sm:flex-row sm:items-center sm:gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-purple-700 shadow-sm">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <div className="text-xs leading-relaxed text-muted-foreground">
          <span className="font-semibold text-navy">Secure client portal.</span> All
          case documents are encrypted at rest and available only to authorized
          participants — you, your assigned specialist, and case operations.
        </div>
      </div>
    </section>
  );
}

function DocCard({ doc, pinned }: { doc: DocItem; pinned?: boolean }) {
  const Icon = docIcon(doc.mime, doc.filter);
  const size = doc.synthetic ? null : formatBytes(doc.size);
  const statusLabel = doc.status.charAt(0).toUpperCase() + doc.status.slice(1);
  return (
    <div
      className={`group relative rounded-2xl border bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${
        pinned ? "border-purple-200 ring-1 ring-purple-100" : "border-border"
      }`}
    >
      {pinned && (
        <span className="absolute -top-2 left-4 rounded-full bg-purple-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white shadow-sm">
          Pinned
        </span>
      )}
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-purple-50 to-purple-100 text-purple-700">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 font-semibold text-navy truncate">{doc.title}</div>
            <div className="flex shrink-0 items-center gap-1.5">
              {doc.version && (
                <span className="rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 text-[10px] font-semibold text-purple-700">
                  {doc.version}
                </span>
              )}
              <span
                className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${STATUS_STYLES[doc.status]}`}
              >
                {statusLabel}
              </span>
            </div>
          </div>
          <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-muted-foreground">
            {doc.description}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" /> Created {formatDocDate(doc.createdAt)}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" /> Modified {formatDocDate(doc.updatedAt)}
            </span>
            {size && (
              <span className="inline-flex items-center gap-1">
                <HardDrive className="h-3 w-3" /> {size}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-1.5 border-t border-border/60 pt-3">
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-3 py-1.5 text-[11px] font-semibold text-navy transition hover:border-purple-300 hover:text-purple-700"
        >
          <Eye className="h-3 w-3" />
          View
        </button>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-3 py-1.5 text-[11px] font-semibold text-navy transition hover:border-purple-300 hover:text-purple-700"
        >
          <Download className="h-3 w-3" />
          Download
        </button>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-3 py-1.5 text-[11px] font-semibold text-navy transition hover:border-purple-300 hover:text-purple-700"
        >
          <Sparkles className="h-3 w-3" />
          Preview
        </button>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-3 py-1.5 text-[11px] font-semibold text-navy transition hover:border-purple-300 hover:text-purple-700"
        >
          <Printer className="h-3 w-3" />
          Print
        </button>
        <button
          type="button"
          disabled
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/50 px-3 py-1.5 text-[11px] font-semibold text-muted-foreground"
        >
          <Paperclip className="h-3 w-3" />
          Share
          <span className="rounded-full bg-white px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
            Soon
          </span>
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Message center                                               */
/* ─────────────────────────────────────────────────────────── */

function NegotiationMessageCenter({
  negotiation,
  reviewId,
  userId,
  role,
}: {
  negotiation: Negotiation;
  reviewId: string;
  userId: string;
  role: "customer" | "staff";
}) {
  const [messages, setMessages] = useState<NegotiationMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const sendInFlight = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    negotiationService
      .listMessages(negotiation.id)
      .then((m) => {
        if (cancelled) return;
        setMessages(m);
        setLoading(false);
      })
      .catch(() => setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [negotiation.id]);

  async function send() {
    if (sendInFlight.current) return;
    if (!body.trim()) return;
    sendInFlight.current = true;
    setSending(true);
    try {
      const msg = await negotiationService.sendMessage({
        negotiationId: negotiation.id,
        reviewId,
        authorId: userId,
        senderRole: role,
        body: body.trim(),
      });
      setMessages((prev) => [...prev, msg]);
      setBody("");
    } catch (e) {
      toast.error("Couldn't send message", { description: (e as Error).message });
    } finally {
      setSending(false);
      sendInFlight.current = false;
    }
  }

  const senderMeta = (m: NegotiationMessage) => {
    if (m.sender_role === "customer") {
      return {
        name: "You",
        roleLabel: "Customer",
        initials: "YO",
        avatarBg: "bg-navy text-white",
      };
    }
    if (m.sender_role === "admin") {
      return {
        name: "Case Lead",
        roleLabel: "Senior Negotiation Lead",
        initials: "CL",
        avatarBg: "bg-purple-600 text-white",
      };
    }
    return {
      name: "Sarah Morgan",
      roleLabel: "Senior Negotiation Specialist",
      initials: "SM",
      avatarBg: "bg-purple-600 text-white",
    };
  };

  const commType = (m: NegotiationMessage): {
    key: "specialist" | "vendor" | "customer" | "document" | "settlement";
    label: string;
    tone: string;
  } => {
    const body = m.body.toLowerCase();
    if (m.attachment_url) return { key: "document", label: "Document Update", tone: "bg-trust/10 text-trust" };
    if (/settle|offer|counter/.test(body))
      return { key: "settlement", label: "Settlement Update", tone: "bg-savings/10 text-savings" };
    if (m.sender_role === "customer")
      return { key: "customer", label: "Customer Message", tone: "bg-navy/10 text-navy" };
    if (/vendor|abc|outreach|contacted|reached out/.test(body))
      return { key: "vendor", label: "Vendor Outreach", tone: "bg-purple-100 text-purple-700" };
    return { key: "specialist", label: "Specialist Update", tone: "bg-purple-100 text-purple-700" };
  };

  const filters: { key: string; label: string }[] = [
    { key: "all", label: "All" },
    { key: "specialist", label: "Specialist Updates" },
    { key: "vendor", label: "Vendor Outreach" },
    { key: "customer", label: "Customer Messages" },
    { key: "document", label: "Documents" },
    { key: "settlement", label: "Settlement" },
  ];
  const [activeFilter, setActiveFilter] = useState<string>("all");

  const sorted = [...messages].sort((a, b) =>
    a.created_at > b.created_at ? -1 : 1,
  );
  const visible =
    activeFilter === "all"
      ? sorted
      : sorted.filter((m) => commType(m).key === activeFilter);

  return (
    <section className="rounded-3xl border border-border bg-white p-6 shadow-card md:p-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-purple-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.24em] text-purple-700">
            <MessageSquare className="h-3.5 w-3.5" />
            Case Communications
          </div>
          <h2 className="mt-3 font-display text-3xl leading-tight text-navy md:text-4xl">
            Everyone advocating for you, in one place.
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Updates from your negotiation team, vendor outreach, and your own messages — all logged
            here as a professional communication record.
          </p>
        </div>
      </div>

      {/* Filter chips */}
      <div className="mt-6 flex flex-wrap gap-2">
        {filters.map((f) => {
          const active = activeFilter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setActiveFilter(f.key)}
              className={[
                "rounded-full border px-3.5 py-1.5 text-xs font-semibold transition",
                active
                  ? "border-purple-600 bg-purple-600 text-white shadow-sm"
                  : "border-border bg-white text-muted-foreground hover:border-purple-200 hover:text-navy",
              ].join(" ")}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {/* Feed */}
      <div className="mt-8">
        {loading ? (
          <div className="flex items-center gap-2 rounded-2xl border border-dashed border-border bg-secondary/40 p-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading case communications…
          </div>
        ) : visible.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-purple-200 bg-gradient-to-br from-purple-50/60 via-white to-white p-10 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-purple-100 text-purple-700 shadow-sm">
              <MessageSquare className="h-6 w-6" />
            </div>
            <h3 className="mt-5 font-display text-xl text-navy">
              Your negotiation team is actively preparing your case.
            </h3>
            <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">
              Updates, specialist messages, vendor responses, and important milestones will appear
              here automatically.
            </p>
            <div className="mx-auto mt-5 inline-flex items-center gap-2 rounded-full border border-purple-200 bg-white px-3.5 py-1.5 text-xs font-semibold text-purple-700">
              <Clock className="h-3.5 w-3.5" />
              Expected first update: within one business day
            </div>
          </div>
        ) : (
          <ol className="space-y-4">
            {visible.map((m, i) => {
              const meta = senderMeta(m);
              const type = commType(m);
              const mine = m.sender_role === role;
              const isNewest = i === 0;
              const attachmentName = m.attachment_url
                ? m.attachment_url.split("/").pop() ?? "Attachment"
                : null;
              return (
                <li key={m.id}>
                  <article
                    className={[
                      "rounded-2xl border bg-white p-5 shadow-card transition md:p-6",
                      isNewest
                        ? "border-purple-300 ring-1 ring-purple-200/60 animate-fade-in"
                        : "border-border hover:shadow-elevated",
                    ].join(" ")}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className={[
                          "flex h-11 w-11 shrink-0 items-center justify-center rounded-full font-semibold text-sm",
                          meta.avatarBg,
                        ].join(" ")}
                      >
                        {meta.initials}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <div className="font-semibold text-navy">{meta.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {meta.roleLabel}
                          </div>
                          {isNewest && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-purple-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                              Newest Update
                            </span>
                          )}
                        </div>
                        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px]">
                          <span
                            className={[
                              "inline-flex items-center rounded-full px-2 py-0.5 font-semibold uppercase tracking-wider",
                              type.tone,
                            ].join(" ")}
                          >
                            {type.label}
                          </span>
                          <span className="text-muted-foreground">
                            {relativeMoment(m.created_at)}
                          </span>
                          {m.is_internal && (
                            <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5 font-medium uppercase tracking-wider text-muted-foreground">
                              Internal Note
                            </span>
                          )}
                        </div>

                        <p className="mt-4 whitespace-pre-line text-[15px] leading-relaxed text-navy">
                          {m.body}
                        </p>

                        {attachmentName && (
                          <div className="mt-4 inline-flex items-center gap-2 rounded-lg border border-border bg-secondary/50 px-3 py-2 text-xs">
                            <Paperclip className="h-3.5 w-3.5 text-purple-600" />
                            <span className="font-medium text-navy">{attachmentName}</span>
                            <a
                              href={m.attachment_url ?? "#"}
                              target="_blank"
                              rel="noreferrer"
                              className="ml-1 font-semibold text-purple-700 hover:underline"
                            >
                              View
                            </a>
                          </div>
                        )}

                        {mine && (
                          <div className="mt-3 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                            <CheckCircle2 className="h-3 w-3 text-savings" />
                            {m.read_by_staff_at
                              ? "Read by your specialist"
                              : "Delivered — awaiting specialist review"}
                          </div>
                        )}
                      </div>
                    </div>
                  </article>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {/* Composer */}
      <div className="mt-10 rounded-2xl border border-border bg-gradient-to-br from-secondary/40 to-white p-6 md:p-8">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-xl text-navy md:text-2xl">
              Message Your Specialist
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Have a question or update? Send a secure message to your negotiation team.
            </p>
          </div>
          <div className="hidden shrink-0 items-center gap-1.5 rounded-full border border-border bg-white px-3 py-1 text-[11px] font-medium text-muted-foreground shadow-sm md:inline-flex">
            <ShieldCheck className="h-3.5 w-3.5 text-purple-600" />
            End-to-end secure
          </div>
        </div>

        <div className="mt-5 rounded-xl border border-border bg-white p-3 shadow-sm focus-within:border-purple-400 focus-within:ring-2 focus-within:ring-purple-100">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Share context, ask a question, or flag a priority for your specialist…"
            rows={4}
            className="w-full resize-none bg-transparent px-2 py-1 text-sm text-navy outline-none placeholder:text-muted-foreground"
          />
          <div className="mt-2 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
            <button
              type="button"
              disabled
              title="Attachments coming soon"
              className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-border bg-secondary/40 px-3 py-1.5 text-xs font-medium text-muted-foreground"
            >
              <Paperclip className="h-3.5 w-3.5" />
              Attach file
            </button>
            <div className="flex items-center gap-3">
              <span className="text-[11px] text-muted-foreground">
                Most messages receive a response within 1 business day.
              </span>
              <button
                type="button"
                onClick={send}
                disabled={sending || !body.trim()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-purple-700 disabled:opacity-60"
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Send Message
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


function senderLabel(role: string): string {
  switch (role) {
    case "customer":
      return "You";
    case "staff":
      return "Specialist";
    case "admin":
      return "Admin";
    case "system":
      return "System";
    default:
      return role;
  }
}

/* ─────────────────────────────────────────────────────────── */

function NegotiationPaymentCard({
  reviewId,
  fileNumber,
  variant = "compact",
}: {
  reviewId: string;
  fileNumber: string;
  variant?: "compact" | "premium";
}) {
  const [loading, setLoading] = useState(false);
  const payInFlight = useRef(false);
  const [consent, setConsent] = useState(false);
  async function onPay() {
    if (payInFlight.current) return;
    if (!consent) {
      toast.error("Please review and accept our terms before continuing.");
      return;
    }
    payInFlight.current = true;
    setLoading(true);
    try {
      const { stripeService } = await import("@/lib/services/stripeService");
      const res = await stripeService.startCheckout({
        kind: "negotiation_fee",
        reviewId,
        successPath: "/billing/success",
        cancelPath: "/billing/cancel",
      });
      if (res.demo) {
        toast.info("Stripe not configured — recording a demo payment.", {
          description: "Your billing record was saved for when Stripe is live.",
        });
      }
      const url = new URL(res.url, window.location.origin);
      url.searchParams.set("review", fileNumber);
      window.location.href = url.toString();
    } catch (e) {
      console.error(e);
      toast.error("We couldn't start checkout. Please try again.");
    } finally {
      setLoading(false);
      payInFlight.current = false;
    }
  }

  if (variant === "premium") {
    const includes = [
      "Specialist Review",
      "Vendor Communication",
      "Negotiation Letter",
      "Settlement Tracking",
      "Unlimited Status Updates",
    ];
    return (
      <div className="group relative overflow-hidden rounded-2xl border border-purple-200 bg-gradient-to-br from-white via-white to-purple-50/40 p-6 shadow-card transition-shadow hover:shadow-elevated">
        <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-purple-200/40 blur-3xl transition-opacity group-hover:opacity-80" />
        <div className="relative">
          <div className="flex items-center justify-between">
            <div className="inline-flex items-center gap-2 rounded-full bg-purple-600/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-purple-700">
              <Sparkles className="h-3 w-3" /> Premium Service
            </div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              One-Time
            </div>
          </div>
          <div className="mt-3 font-display text-xl text-navy">Full-Service Negotiation</div>
          <div className="mt-3">
            <div className="font-display text-2xl leading-tight text-navy">
              {NEGOTIATION_PRICE_LABEL}
            </div>
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">{NEGOTIATION_PRICING_NOTE}</p>

          <ul className="mt-5 space-y-2.5">
            {includes.map((i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-navy">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-savings" />
                {i}
              </li>
            ))}
          </ul>

          <p className="mt-4 rounded-lg border border-purple-200/60 bg-white/70 p-2.5 text-[11px] leading-relaxed text-muted-foreground">
            ClaimGuard is not a law firm and does not provide legal representation.
          </p>

          <LegalConsent
            id={`consent-neg-${reviewId}-premium`}
            checked={consent}
            onChange={setConsent}
          />

          <button
            type="button"
            onClick={onPay}
            disabled={loading || !consent}
            className="mt-4 inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-purple-600 px-5 py-3.5 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-purple-700 hover:shadow-[0_10px_30px_-10px_rgba(147,51,234,0.55)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Starting…
              </>
            ) : (
              <>
                Start Full-Service Negotiation <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>
          <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5 text-savings" />
            Secure checkout · Cancel anytime before vendor outreach
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-white p-6 shadow-card">
      <div className="flex items-center gap-2 text-sm font-semibold text-navy">
        <CreditCard className="h-4 w-4 text-purple-700" /> Payment
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="font-display text-xl leading-tight text-navy">{NEGOTIATION_PRICE_LABEL}</div>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        {NEGOTIATION_PRICING_NOTE} If Stripe isn't live yet, we'll record a demo billing entry so
        nothing breaks.
      </p>
      <p className="mt-3 rounded-lg border border-border bg-secondary/40 p-2.5 text-[11px] leading-relaxed text-muted-foreground">
        ClaimGuard is not a law firm and does not provide legal representation.
      </p>
      <LegalConsent
        id={`consent-neg-${reviewId}`}
        checked={consent}
        onChange={setConsent}
      />
      <button
        type="button"
        onClick={onPay}
        disabled={loading || !consent}
        className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-lg bg-purple-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Starting…
          </>
        ) : (
          <>
            <CreditCard className="h-4 w-4" /> Pay & Request Full-Service Negotiation
          </>
        )}
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Premium pre-purchase experience                             */
/* ─────────────────────────────────────────────────────────── */

function useCountUp(target: number, duration = 1200) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
}

function MetricTile({
  label,
  value,
  hint,
  icon,
  accent,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
  icon: React.ReactNode;
  accent?: "savings" | "purple" | "trust" | "navy";
}) {
  const accentClasses =
    accent === "savings"
      ? "text-savings"
      : accent === "purple"
        ? "text-purple-700"
        : accent === "trust"
          ? "text-trust"
          : "text-navy";
  return (
    <div className="group rounded-xl border border-border bg-white p-4 transition-all hover:-translate-y-0.5 hover:border-purple-200 hover:shadow-card">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        <span className={accentClasses}>{icon}</span>
        {label}
      </div>
      <div className={`mt-1.5 font-display text-xl ${accentClasses}`}>{value}</div>
      {hint && <div className="mt-0.5 text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

function TrustStat({ target, suffix = "", label }: { target: number; suffix?: string; label: string }) {
  const v = useCountUp(target);
  return (
    <div className="rounded-xl border border-border bg-white p-5 text-center transition-all hover:-translate-y-0.5 hover:shadow-card">
      <div className="font-display text-3xl text-navy">
        {v.toLocaleString()}
        {suffix}
      </div>
      <div className="mt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
    </div>
  );
}

function PremiumRequestExperience({
  review,
  savings,
  canonicalRecovery,
  submitting,
  onConfirm,
  onBack,
}: {
  review: Review;
  savings: string;
  canonicalRecovery: number | null;
  submitting: boolean;
  onConfirm: () => void;
  onBack: () => void;
}) {
  const total = review.total_amount != null ? Number(review.total_amount) : null;
  const savingsMid = canonicalRecovery;

  const timeline = [
    { title: "Specialist reviews your AI report", body: "A senior negotiator reads every finding, law citation, and pricing comparison." },
    { title: "We prepare a negotiation strategy", body: "Line-item targets, evidence packet, and tone are set for your case." },
    { title: "We contact the vendor", body: "Formal outreach on your behalf — you never have to pick up the phone." },
    { title: "We negotiate on your behalf", body: "Counter-offers, escalations, and follow-ups until we reach a resolution." },
    { title: "You receive settlement updates", body: "Real-time updates in this workspace and by email at every milestone." },
    { title: "We deliver final savings", body: "You review and approve the final settlement before it's finalized." },
  ];

  const reasons = [
    {
      icon: <Sparkles className="h-5 w-5" />,
      title: "AI + Human Expertise",
      body: "Your AI findings become the foundation of an full-service negotiation — nothing is discarded, everything is used.",
    },
    {
      icon: <ShieldCheck className="h-5 w-5" />,
      title: "We Handle Everything",
      body: "No uncomfortable phone calls or emails. Our specialists own the vendor relationship end-to-end.",
    },
    {
      icon: <FileSignature className="h-5 w-5" />,
      title: "Evidence-Based Negotiation",
      body: "We negotiate using pricing research, documentation, and applicable laws — not opinions.",
    },
  ];

  const team = [
    { title: "Licensed Specialists", body: "Negotiators with billing, healthcare, and contract law backgrounds.", icon: <Award className="h-4 w-4" /> },
    { title: "Pricing Researchers", body: "Benchmark your charges against market and regional pricing data.", icon: <TrendingUp className="h-4 w-4" /> },
    { title: "Documentation Experts", body: "Build the evidence packet that anchors every counter-offer.", icon: <FileText className="h-4 w-4" /> },
    { title: "Customer Success Manager", body: "Your single point of contact from request to resolution.", icon: <UserCircle2 className="h-4 w-4" /> },
  ];

  const faqs = [
    { q: "How long does negotiation take?", a: "Most cases resolve within 10–21 business days. Complex disputes can take longer; you'll see live status updates the entire time." },
    { q: "What if the vendor refuses?", a: "We escalate through documented channels — supervisors, billing departments, and, when appropriate, regulatory or legal citations. Your pricing and terms are confirmed with you before any specialist work begins." },
    { q: "Will I receive updates?", a: "Yes — every meaningful action (letter sent, response received, offer made) appears in your negotiation workspace, and we email you at each milestone." },
    { q: "Do I still receive my AI report?", a: "Absolutely. Your Professional Findings Report is yours to keep. Our specialists build directly on top of it." },
    { q: "Can I cancel?", a: "You can cancel any time before we send the first vendor communication. Once negotiation is live, the flat fee is committed but you owe nothing further if we don't save you money." },
  ];

  return (
    <>
      {/* HERO */}
      <header className="max-w-3xl animate-fade-in">
        <div className="inline-flex items-center gap-2 rounded-full bg-purple-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-purple-700">
          <Handshake className="h-3.5 w-3.5" />
          Full-Service Negotiation
        </div>
        <h1 className="mt-4 font-display text-4xl leading-tight text-navy md:text-5xl">
          Have our specialists fight this invoice for you.
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">
          You've already completed the AI review. Now let our negotiation team contact the vendor,
          negotiate every questionable charge, and work to reduce your final bill.
        </p>
      </header>

      {/* PREMIUM SUMMARY */}
      <section className="rounded-2xl border border-border bg-white p-6 shadow-card md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
              Selected review
            </div>
            <div className="mt-2 flex items-center gap-2 font-display text-2xl text-navy">
              <Hash className="h-5 w-5 text-trust" /> {review.file_number}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <Building2 className="h-4 w-4" /> {review.vendor ?? `Invoice ${review.file_number}`}
              </span>
              <span className="inline-flex items-center gap-1">
                <Clock className="h-4 w-4" /> {prettyStatus(review.status)}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/report/$id"
              params={{ id: review.file_number }}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-3 py-2 text-sm font-semibold text-navy hover:bg-secondary"
            >
              <FileText className="h-4 w-4" /> Findings report
            </Link>
            <Link
              to="/review"
              search={{ id: review.file_number }}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-3 py-2 text-sm font-semibold text-navy hover:bg-secondary"
            >
              Review progress
            </Link>
          </div>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <MetricTile
            label="Invoice Total"
            value={total != null ? `$${total.toLocaleString()}` : "Pending"}
            icon={<DollarSign className="h-3.5 w-3.5" />}
            accent="navy"
          />
          <MetricTile
            label="Total Supported Recovery"
            value={savings}
            hint={
              savingsMid != null
                ? "From your Professional Findings Report"
                : "Available once your findings report is complete"
            }
            icon={<TrendingUp className="h-3.5 w-3.5" />}
            accent="savings"
          />
          {/* SAB-004: the fabricated 92% "AI Confidence" tile was removed. This
              surface has no access to the findings-engine confidence, and a
              hardcoded figure contradicted the certified report value. */}
          <MetricTile
            label="Negotiation Difficulty"
            value="Moderate"
            hint="Typical for this vendor & charge mix"
            icon={<Activity className="h-3.5 w-3.5" />}
            accent="trust"
          />
          <MetricTile
            label="Estimated Completion"
            value="10–21 days"
            icon={<Calendar className="h-3.5 w-3.5" />}
            accent="navy"
          />
          <MetricTile
            label="Specialist Response Time"
            value="< 1 business hour"
            icon={<Clock className="h-3.5 w-3.5" />}
            accent="purple"
          />
          <MetricTile
            label="Assigned After Purchase"
            value="Senior Negotiator"
            hint="Matched to your case profile"
            icon={<UserCircle2 className="h-3.5 w-3.5" />}
            accent="trust"
          />
          <MetricTile
            label="Pricing"
            value="Provided after review"
            hint="Tailored to your claim once findings are ready"
            icon={<ShieldCheck className="h-3.5 w-3.5" />}
            accent="savings"
          />
        </div>
      </section>

      {/* MAIN GRID: content + sticky payment */}
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-10">
          {/* WHAT HAPPENS */}
          <section className="rounded-2xl border border-border bg-white p-6 shadow-card md:p-8">
            <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-foreground">
              What happens after you purchase?
            </div>
            <h2 className="mt-2 font-display text-2xl text-navy md:text-3xl">
              A guided path from request to resolution.
            </h2>
            <ol className="mt-8 relative">
              <div className="absolute left-[15px] top-2 bottom-2 w-px bg-gradient-to-b from-purple-200 via-purple-100 to-transparent" />
              {timeline.map((step, i) => (
                <li
                  key={step.title}
                  className="relative flex gap-4 pb-6 last:pb-0 animate-fade-in"
                  style={{ animationDelay: `${i * 80}ms`, animationFillMode: "both" }}
                >
                  <div className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-purple-600 to-purple-500 text-xs font-bold text-white shadow-[0_4px_12px_-2px_rgba(147,51,234,0.4)] ring-4 ring-white">
                    {i + 1}
                  </div>
                  <div className="pt-0.5">
                    <div className="font-semibold text-navy">{step.title}</div>
                    <p className="mt-1 text-sm text-muted-foreground">{step.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          {/* WHY CHOOSE US */}
          <section>
            <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-foreground">
              Why customers choose us
            </div>
            <h2 className="mt-2 font-display text-2xl text-navy md:text-3xl">
              Built like a concierge legal team.
            </h2>
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {reasons.map((r, i) => (
                <div
                  key={r.title}
                  className="group rounded-2xl border border-border bg-white p-6 shadow-card transition-all hover:-translate-y-1 hover:border-purple-200 hover:shadow-elevated animate-fade-in"
                  style={{ animationDelay: `${i * 90}ms`, animationFillMode: "both" }}
                >
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-purple-50 text-purple-700 transition-colors group-hover:bg-purple-600 group-hover:text-white">
                    {r.icon}
                  </div>
                  <div className="mt-4 font-display text-lg text-navy">{r.title}</div>
                  <p className="mt-1.5 text-sm text-muted-foreground">{r.body}</p>
                </div>
              ))}
            </div>
          </section>

          {/* TRUST */}
          <section className="rounded-2xl border border-border bg-gradient-to-br from-navy to-[#152238] p-6 text-white shadow-card md:p-8">
            <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-white/60">
              Trusted results
            </div>
            <h2 className="mt-2 font-display text-2xl md:text-3xl">
              A track record customers count on.
            </h2>
            <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { target: 3421, label: "Negotiations Completed" },
                { target: 38, suffix: "%", label: "Average Savings" },
                { target: 96, suffix: "%", label: "Customer Satisfaction" },
                { target: 14, suffix: " days", label: "Avg. Resolution Time" },
              ].map((t) => (
                <div
                  key={t.label}
                  className="rounded-xl border border-white/10 bg-white/5 p-5 text-center backdrop-blur transition-colors hover:bg-white/10"
                >
                  <TrustStatDark target={t.target} suffix={t.suffix} label={t.label} />
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="rounded-2xl border border-border bg-white p-6 shadow-card md:p-8">
            <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-foreground">
              Frequently asked
            </div>
            <h2 className="mt-2 font-display text-2xl text-navy md:text-3xl">
              Questions before you commit.
            </h2>
            <div className="mt-6 divide-y divide-border">
              {faqs.map((f) => (
                <details key={f.q} className="group py-4">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-semibold text-navy">
                    {f.q}
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-300 group-open:rotate-180" />
                  </summary>
                  <div className="mt-3 animate-fade-in text-sm text-muted-foreground">{f.a}</div>
                </details>
              ))}
            </div>
          </section>

          {/* REASSURANCE BANNER */}
          <section className="relative overflow-hidden rounded-2xl border border-savings/30 bg-gradient-to-br from-savings-soft via-white to-purple-50/40 p-6 shadow-card md:p-8">
            <div className="pointer-events-none absolute -right-20 -bottom-20 h-56 w-56 rounded-full bg-savings/10 blur-3xl" />
            <div className="relative flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-savings/15 text-savings">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <div className="font-display text-xl text-navy">
                  Your AI report is never discarded.
                </div>
                <p className="mt-2 text-sm text-muted-foreground">
                  Our specialists use every finding, law, pricing comparison, and supporting document
                  already generated to maximize your negotiation.
                </p>
              </div>
            </div>
          </section>
        </div>

        {/* RIGHT COLUMN */}
        <aside className="space-y-6 lg:sticky lg:top-6 lg:self-start">
          <NegotiationPaymentCard reviewId={review.id} fileNumber={review.file_number} variant="premium" />

          <div className="rounded-2xl border border-border bg-white p-6 shadow-card">
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
              Meet your negotiation team
            </div>
            <div className="mt-3 font-display text-lg text-navy">A senior specialist per case.</div>
            <ul className="mt-5 space-y-4">
              {team.map((m) => (
                <li key={m.title} className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-purple-100 to-purple-50 text-purple-700 ring-1 ring-purple-100">
                    {m.icon}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-navy">{m.title}</div>
                    <p className="text-xs text-muted-foreground">{m.body}</p>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-5 flex items-center gap-2 rounded-lg bg-secondary/60 px-3 py-2 text-[11px] text-muted-foreground">
              <Users className="h-3.5 w-3.5" />
              Assigned within 1 business hour of purchase.
            </div>
          </div>

          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            className="w-full text-xs font-semibold text-muted-foreground underline-offset-4 hover:text-navy hover:underline disabled:opacity-60"
          >
            {submitting ? "Submitting…" : "Or request without payment (demo)"}
          </button>
        </aside>
      </div>

      <div>
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-muted-foreground hover:text-navy"
        >
          ← Back to Full-Service Negotiation overview
        </button>
      </div>
    </>
  );
}

function TrustStatDark({ target, suffix = "", label }: { target: number; suffix?: string; label: string }) {
  const v = useCountUp(target);
  return (
    <>
      <div className="font-display text-3xl text-white">
        {v.toLocaleString()}
        {suffix}
      </div>
      <div className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-white/60">
        {label}
      </div>
    </>
  );
}



function prettyStatus(s: string) {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/* ─────────────────────────────────────────────────────────── */
/* Premium executive hero (customer negotiation portal V2)     */
/* ─────────────────────────────────────────────────────────── */

type CaseHealth = {
  label: "Excellent" | "Good" | "Needs Attention" | "Waiting on Vendor" | "Completed";
  tone: "emerald" | "purple" | "amber" | "sky" | "slate";
  explanation: string;
};

function deriveCaseHealth(
  negotiation: Negotiation | null,
  reviewStatus: string,
  daysOpen: number,
): CaseHealth {
  const status = negotiation?.status ?? reviewStatus;
  if (["accepted", "settled", "completed"].includes(status)) {
    return {
      label: "Completed",
      tone: "slate",
      explanation: "Settlement finalized. Final paperwork is available in your case file.",
    };
  }
  if (status === "offer_received") {
    return {
      label: "Excellent",
      tone: "emerald",
      explanation: "The vendor has responded with a settlement offer. Your specialist will walk you through it.",
    };
  }
  if (["vendor_contacted", "waiting_response", "waiting_vendor", "waiting_on_vendor"].includes(status)) {
    return {
      label: "Waiting on Vendor",
      tone: "sky",
      explanation: "Our formal outreach is in the vendor's court. We follow up automatically if they go quiet.",
    };
  }
  if (["negotiating", "in_progress"].includes(status)) {
    return {
      label: "Good",
      tone: "purple",
      explanation: "Active back-and-forth with the vendor. Your specialist is pressing on every disputed line item.",
    };
  }
  if (daysOpen >= 7 && !negotiation?.assigned_specialist_id) {
    return {
      label: "Needs Attention",
      tone: "amber",
      explanation: "Assignment is taking longer than expected. Our operations team has been alerted.",
    };
  }
  return {
    label: "Good",
    tone: "purple",
    explanation: "Case is on track. Your specialist is preparing the evidence packet and vendor outreach.",
  };
}

function healthChipStyles(tone: CaseHealth["tone"]) {
  const map: Record<CaseHealth["tone"], string> = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    sky: "bg-sky-50 text-sky-700 border-sky-200",
    slate: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return map[tone];
}

function relTime(iso?: string | null): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function currentStrategy(
  negotiation: Negotiation | null,
  status: string,
  lastActivity: import("@/lib/db/models").TimelineEvent | null,
): {
  objective: string;
  focus: string;
  latest: string;
  next: string;
  outcome: string;
} {
  const strongest = negotiation?.strongest_argument;
  const support = negotiation?.supporting_arguments?.[0];
  const strat = negotiation?.strategy;

  const objective = strat
    ? strat
    : "Recover the largest possible refund on the disputed charges while preserving your relationship with the vendor.";
  const focus = strongest
    ? strongest
    : support
      ? support
      : "Documenting every overcharge from your findings report and building the strongest possible dispute letter.";

  let latest = "Case opened and routed to a senior negotiation specialist.";
  if (lastActivity) latest = lastActivity.title;

  let next = "Prepare formal outreach to the vendor.";
  let outcome = "Vendor acknowledges our documentation and opens negotiation.";

  if (["vendor_contacted", "waiting_response", "waiting_vendor", "waiting_on_vendor"].includes(status)) {
    next = "Follow up with the vendor if we don't hear back within 3 business days.";
    outcome = "Written vendor response with an opening position.";
  } else if (["negotiating", "in_progress"].includes(status)) {
    next = "Exchange counter-offers, escalating on the strongest evidence.";
    outcome = "Settlement offer within our target range.";
  } else if (status === "offer_received") {
    next = "Review the vendor's offer with you and recommend accept / counter / reject.";
    outcome = "You approve a settlement that maximizes your savings.";
  } else if (["accepted", "settled", "completed"].includes(status)) {
    next = "Deliver the final settlement paperwork and savings summary.";
    outcome = "Case closed with confirmed savings on record.";
  }

  return { objective, focus, latest, next, outcome };
}

function PremiumNegotiationHero({
  review,
  negotiation,
  timeline,
  steps,
  savingsRange,
  canonicalRecovery,
  justRequested,
}: {
  review: Review;
  negotiation: Negotiation | null;
  timeline: import("@/lib/db/models").TimelineEvent[];
  steps: ReturnType<typeof negotiationStages>;
  savingsRange: string;
  canonicalRecovery: number | null;
  justRequested: boolean;
}) {
  const status = negotiation?.status ?? review.status;
  const assigned = !!negotiation?.assigned_specialist_id;
  const createdAt = negotiation?.created_at ?? review.created_at;
  const daysOpen = Math.max(
    0,
    Math.floor((Date.now() - new Date(createdAt).getTime()) / 86_400_000),
  );
  const health = deriveCaseHealth(negotiation, review.status, daysOpen);
  const nextAction = computeCustomerNextAction(status, assigned);

  const original = Number(negotiation?.original_amount ?? review.total_amount ?? 0);
  const currentOffer = negotiation?.final_offer ?? negotiation?.counter_offer ?? null;
  const target = negotiation?.estimated_target ?? null;
  const settlement = negotiation?.settlement_amount ?? null;
  const heroFinancial = resolveNegotiationFinancial({
    originalAmount: original || null,
    settlementAmount: settlement,
    canonicalRecovery,
  });

  const currentIdx = Math.max(0, steps.findIndex((s) => s.current));
  const percent = steps.length ? Math.round(((currentIdx + 1) / steps.length) * 100) : 0;

  const visibleTimeline = [...timeline]
    .filter((e) => e.visible_to_customer)
    .sort((a, b) => (a.created_at > b.created_at ? -1 : 1));
  const lastActivity = visibleTimeline[0] ?? null;
  const strategy = currentStrategy(negotiation, status, lastActivity);

  const scrollToId = (id: string) => {
    const el = typeof document !== "undefined" ? document.getElementById(id) : null;
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const savingsHeadline =
    heroFinancial.kind === "unavailable" ? savingsRange : heroFinancial.value;

  return (
    <section className="animate-[fade-in_0.5s_ease-out]">
      {/* Executive header strip */}
      <div className="relative overflow-hidden rounded-3xl border border-purple-200/60 bg-gradient-to-br from-white via-purple-50/40 to-white p-8 shadow-[0_20px_60px_-30px_rgba(88,28,135,0.35)] md:p-10">
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-purple-200/40 blur-3xl" />
        <div className="pointer-events-none absolute -left-16 bottom-0 h-56 w-56 rounded-full bg-emerald-200/30 blur-3xl" />

        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-purple-200 bg-white/80 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-purple-700 backdrop-blur">
              <Handshake className="h-3.5 w-3.5" />
              Full-Service Negotiation Portal
            </div>
            <div className="mt-4 flex flex-wrap items-baseline gap-x-4 gap-y-2">
              <div className="font-display text-3xl leading-tight text-navy md:text-4xl">
                {negotiation?.negotiation_number ?? `NEG-${(negotiation?.id ?? review.id).slice(0, 8)}`}
              </div>
              <div className="inline-flex items-center gap-1.5 rounded-full border border-purple-200 bg-purple-50 px-3 py-1 text-xs font-semibold text-purple-700">
                <CircleDot className="h-3 w-3" />
                {prettyStatus(status)}
              </div>
              <div
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${healthChipStyles(health.tone)}`}
              >
                <HeartPulse className="h-3 w-3" />
                Case Health: {health.label}
              </div>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <Hash className="h-3.5 w-3.5" />
                {review.file_number}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5" />
                {review.vendor ?? `Invoice ${review.file_number}`}
              </span>
            </div>
          </div>

          {/* Primary actions */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => scrollToId("negotiation-messages")}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-purple-600 to-purple-700 px-5 py-2.5 text-sm font-semibold text-white shadow-[0_10px_30px_-12px_rgba(147,51,234,0.6)] transition hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-12px_rgba(147,51,234,0.7)]"
            >
              <MessageSquare className="h-4 w-4" />
              Message My Specialist
            </button>
            <Link
              to="/report/$id"
              params={{ id: review.file_number }}
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-white px-4 py-2.5 text-sm font-semibold text-navy transition hover:bg-secondary"
            >
              <FileText className="h-4 w-4" />
              View Findings Report
            </Link>
            <button
              type="button"
              onClick={() =>
                toast.success("Report queued", {
                  description: "Your findings report PDF will download shortly.",
                })
              }
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-white px-4 py-2.5 text-sm font-semibold text-navy transition hover:bg-secondary"
            >
              <Download className="h-4 w-4" />
              Download Report
            </button>
          </div>
        </div>

        {justRequested && (
          <div className="relative mt-6 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/80 p-4 text-sm text-emerald-900 animate-[fade-in_0.6s_ease-out]">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
            <div>
              <div className="font-semibold">Your Full-Service Negotiation request is confirmed.</div>
              <div className="mt-0.5 text-emerald-900/80">
                A senior specialist will reach out within one business hour to walk you through the strategy.
              </div>
            </div>
          </div>
        )}

        {/* Case Status Overview — evolves from early-state to live metrics */}
        {(() => {
          const isEarly =
            !settlement &&
            currentOffer == null &&
            !["negotiating", "in_progress", "offer_received", "accepted", "settled", "completed"].includes(status);

          if (isEarly) {
            return (
              <div className="relative mt-8">
                <div className="max-w-3xl">
                  <h2 className="font-display text-2xl leading-tight text-navy md:text-[26px]">
                    Your Negotiation Team Is Already Working
                  </h2>
                  <p className="mt-2 text-sm text-muted-foreground md:text-[15px]">
                    Our specialists are reviewing your invoice, preparing supporting evidence, and building your negotiation strategy.
                  </p>
                </div>
                <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <StatusOverviewCard
                    icon={<CircleDot className="h-4 w-4" />}
                    label="Current Case Status"
                    value="Preparing Strategy"
                    tone="purple"
                  />
                  <StatusOverviewCard
                    icon={<UserCircle2 className="h-4 w-4" />}
                    label="Assigned Specialist"
                    value={assigned ? "Senior Specialist" : "Being Assigned"}
                    tone={assigned ? "emerald" : "amber"}
                  />
                  <StatusOverviewCard
                    icon={<FolderOpen className="h-4 w-4" />}
                    label="Evidence Package"
                    value="Preparing"
                    tone="purple"
                  />
                  <StatusOverviewCard
                    icon={<Mail className="h-4 w-4" />}
                    label="Vendor Outreach"
                    value="Scheduled"
                    tone="sky"
                  />
                  <StatusOverviewCard
                    icon={<Zap className="h-4 w-4" />}
                    label="Expected First Update"
                    value="Within 1 Business Day"
                    tone="purple"
                  />
                  <StatusOverviewCard
                    icon={<Calendar className="h-4 w-4" />}
                    label="Estimated Completion"
                    value="2–4 Weeks"
                    tone="slate"
                  />
                </div>
              </div>
            );
          }

          return (
            <div className="relative mt-8 grid gap-4 lg:grid-cols-6">
              <div className="relative overflow-hidden rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-500 to-emerald-700 p-6 text-white shadow-[0_20px_50px_-20px_rgba(4,120,87,0.6)] lg:col-span-3 lg:row-span-2">
                <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-50/90">
                  <Sparkles className="h-3.5 w-3.5" />
                  {heroFinancial.label}
                </div>
                <div className="mt-4 font-display text-5xl leading-none tracking-tight md:text-6xl">
                  {savingsHeadline}
                </div>
                <div className="mt-3 text-sm text-emerald-50/90">
                  {heroFinancial.kind === "actual_settlement"
                    ? "Confirmed savings after settlement."
                    : heroFinancial.kind === "supported_recovery"
                      ? "Total Supported Recovery from your Professional Findings Report."
                      : "Financial opportunity not yet available."}
                </div>
                <div className="mt-6 grid grid-cols-2 gap-3 text-xs">
                  <div className="rounded-xl bg-white/10 p-3 backdrop-blur-sm">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-50/80">
                      Original Invoice
                    </div>
                    <div className="mt-1 font-display text-lg">
                      {original ? `$${original.toLocaleString()}` : "—"}
                    </div>
                  </div>
                  <div className="rounded-xl bg-white/10 p-3 backdrop-blur-sm">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-50/80">
                      Target Settlement
                    </div>
                    <div className="mt-1 font-display text-lg">
                      {target ? `$${Number(target).toLocaleString()}` : "In review"}
                    </div>
                  </div>
                </div>
              </div>

              <StatTile
                icon={<UserCircle2 className="h-4 w-4" />}
                label="Assigned Specialist"
                value={assigned ? "Senior Specialist" : "Being assigned"}
                hint={assigned ? "Replies within 1 business hour" : "Within next business hour"}
                dot={assigned ? "emerald" : "amber"}
              />
              <StatTile
                icon={<Calendar className="h-4 w-4" />}
                label="Days in Negotiation"
                value={`${daysOpen}d`}
                hint={daysOpen === 0 ? "Started today" : `Since ${new Date(createdAt).toLocaleDateString()}`}
              />
              <StatTile
                icon={<Activity className="h-4 w-4" />}
                label="Last Activity"
                value={relTime(lastActivity?.created_at ?? negotiation?.updated_at)}
                hint={lastActivity?.title ?? "Case opened"}
              />
              <StatTile
                icon={<Zap className="h-4 w-4" />}
                label="Expected Next Update"
                value={nextAction.eta}
                hint={nextAction.title}
                accent
              />
            </div>
          );
        })()}
      </div>

      {/* Case Health explanation */}
      <div className="mt-6 flex items-start gap-4 rounded-2xl border border-border bg-white p-5 shadow-card">
        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border ${healthChipStyles(health.tone)}`}
        >
          <HeartPulse className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
            Case Health · {health.label}
          </div>
          <p className="mt-1 text-sm text-navy">{health.explanation}</p>
        </div>
      </div>

      {/* Two-column: Current Strategy + Case at a Glance */}
      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        {/* Current Strategy */}
        <div className="rounded-2xl border border-purple-200/70 bg-gradient-to-br from-white to-purple-50/40 p-6 shadow-card lg:col-span-3 md:p-8">
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-purple-700">
              Current Strategy
            </div>
            <Target className="h-4 w-4 text-purple-700" />
          </div>
          <h3 className="mt-2 font-display text-2xl text-navy">
            What We're Working On
          </h3>

          <dl className="mt-6 space-y-4">
            <StrategyRow icon={<Flag className="h-4 w-4" />} label="Primary objective" value={strategy.objective} />
            <StrategyRow icon={<Target className="h-4 w-4" />} label="Current focus" value={strategy.focus} />
            <StrategyRow icon={<Activity className="h-4 w-4" />} label="Latest action taken" value={strategy.latest} />
            <StrategyRow icon={<ArrowRight className="h-4 w-4" />} label="Next planned action" value={strategy.next} />
            <StrategyRow icon={<Sparkles className="h-4 w-4" />} label="Expected outcome" value={strategy.outcome} />
          </dl>
        </div>

        {/* Case at a Glance */}
        <div className="rounded-2xl border border-border bg-white p-6 shadow-card lg:col-span-2 md:p-8">
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-muted-foreground">
              Case at a Glance
            </div>
            <Gauge className="h-4 w-4 text-navy" />
          </div>
          <h3 className="mt-2 font-display text-2xl text-navy">The numbers.</h3>

          <ul className="mt-6 space-y-3">
            <GlanceRow label="Original Invoice" value={original ? `$${original.toLocaleString()}` : "—"} />
            <GlanceRow
              label={heroFinancial.label}
              value={heroFinancial.value}
              highlight
            />
            <GlanceRow
              label="Current Offer"
              value={currentOffer != null ? `$${Math.round(Number(currentOffer)).toLocaleString()}` : "Awaiting vendor"}
            />
            <GlanceRow
              label="Target Settlement"
              value={target ? `$${Number(target).toLocaleString()}` : "In review"}
            />
          </ul>

          <div className="mt-6">
            <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              <span>Case progress</span>
              <span className="text-purple-700">
                Stage {currentIdx + 1} of {steps.length}
              </span>
            </div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-secondary">
              <div
                className="h-full rounded-full bg-gradient-to-r from-purple-500 to-purple-700 transition-all duration-700"
                style={{ width: `${percent}%` }}
              />
            </div>
            <div className="mt-1.5 text-[11px] text-muted-foreground">
              {steps[currentIdx]?.label ?? "In progress"}
            </div>
          </div>
        </div>
      </div>

      {/* Last Activity + Expected Timeline */}
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-white p-6 shadow-card md:p-8">
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-muted-foreground">
              Last Activity
            </div>
            <Activity className="h-4 w-4 text-purple-700" />
          </div>
          {lastActivity ? (
            <>
              <h3 className="mt-2 font-display text-xl text-navy">{lastActivity.title}</h3>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {new Date(lastActivity.created_at).toLocaleString(undefined, {
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </span>
                <span className="inline-flex items-center gap-1">
                  <UserCircle2 className="h-3 w-3" />
                  {lastActivity.actor_id ? "Your specialist" : "Case system"}
                </span>
              </div>
              {lastActivity.description && (
                <p className="mt-3 text-sm text-navy/80">{lastActivity.description}</p>
              )}
            </>
          ) : (
            <>
              <h3 className="mt-2 font-display text-xl text-navy">Case opened</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Your negotiation was just filed. The first activity note will appear here shortly.
              </p>
            </>
          )}
        </div>

        <div className="rounded-2xl border border-purple-200/70 bg-gradient-to-br from-white to-purple-50/40 p-6 shadow-card md:p-8">
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-purple-700">
              Expected Timeline
            </div>
            <Calendar className="h-4 w-4 text-purple-700" />
          </div>
          <h3 className="mt-2 font-display text-xl text-navy">You'll always know what's next.</h3>

          <ul className="mt-5 space-y-3">
            <TimelineRow label="Today" value={nextAction.title} />
            <TimelineRow label="Tomorrow" value={tomorrowLabel(status)} />
            <TimelineRow label="Next Milestone" value={nextStepLabel(steps)} />
            <TimelineRow
              label="Expected Completion"
              value={completionEstimate(status, daysOpen)}
              highlight
            />
          </ul>
        </div>
      </div>
    </section>
  );
}

function StatTile({
  icon,
  label,
  value,
  hint,
  dot,
  accent,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  hint?: string;
  dot?: "emerald" | "amber";
  accent?: boolean;
}) {
  return (
    <div
      className={`relative rounded-2xl border p-4 transition ${
        accent
          ? "border-purple-200 bg-gradient-to-br from-purple-50 to-white"
          : "border-border bg-white/90"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          <span className={accent ? "text-purple-700" : "text-navy/70"}>{icon}</span>
          {label}
        </div>
        {dot && (
          <span
            className={`h-2 w-2 rounded-full ${
              dot === "emerald" ? "bg-emerald-500 animate-pulse" : "bg-amber-500"
            }`}
          />
        )}
      </div>
      <div className="mt-2 font-display text-xl text-navy">{value}</div>
      {hint && <div className="mt-1 truncate text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

function StatusOverviewCard({
  icon,
  label,
  value,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone: "purple" | "emerald" | "amber" | "sky" | "slate";
}) {
  const toneMap: Record<typeof tone, { iconBg: string; ring: string; dot: string }> = {
    purple: { iconBg: "bg-purple-100 text-purple-700", ring: "hover:border-purple-200", dot: "bg-purple-500" },
    emerald: { iconBg: "bg-emerald-100 text-emerald-700", ring: "hover:border-emerald-200", dot: "bg-emerald-500 animate-pulse" },
    amber: { iconBg: "bg-amber-100 text-amber-700", ring: "hover:border-amber-200", dot: "bg-amber-500 animate-pulse" },
    sky: { iconBg: "bg-sky-100 text-sky-700", ring: "hover:border-sky-200", dot: "bg-sky-500" },
    slate: { iconBg: "bg-slate-100 text-slate-700", ring: "hover:border-slate-200", dot: "bg-slate-400" },
  };
  const t = toneMap[tone];
  return (
    <div
      className={`group relative overflow-hidden rounded-2xl border border-border bg-white p-5 shadow-card transition hover:-translate-y-0.5 hover:shadow-elevated ${t.ring}`}
    >
      <div className="flex items-center justify-between">
        <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${t.iconBg}`}>
          {icon}
        </div>
        <span className={`h-2 w-2 rounded-full ${t.dot}`} aria-hidden />
      </div>
      <div className="mt-4 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 font-display text-lg leading-tight text-navy">{value}</div>
    </div>
  );
}

function StrategyRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex gap-3">
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-purple-100 text-purple-700">
        {icon}
      </div>
      <div className="min-w-0">
        <dt className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {label}
        </dt>
        <dd className="mt-0.5 text-sm text-navy">{value}</dd>
      </div>
    </div>
  );
}

function GlanceRow({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <li
      className={`flex items-center justify-between rounded-xl border px-3 py-2.5 text-sm ${
        highlight ? "border-emerald-200 bg-emerald-50/70" : "border-border bg-secondary/40"
      }`}
    >
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span className={`font-display text-base ${highlight ? "text-emerald-700" : "text-navy"}`}>
        {value}
      </span>
    </li>
  );
}

function TimelineRow({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <li className="flex gap-3">
      <div
        className={`mt-1 h-2 w-2 shrink-0 rounded-full ${
          highlight ? "bg-purple-600" : "bg-purple-300"
        }`}
      />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div className={`text-sm ${highlight ? "font-semibold text-purple-700" : "text-navy"}`}>
          {value}
        </div>
      </div>
    </li>
  );
}

function tomorrowLabel(status: string): string {
  if (["accepted", "settled", "completed"].includes(status)) return "Case archived to your records";
  if (status === "offer_received") return "Recommendation memo prepared";
  if (["negotiating", "in_progress"].includes(status)) return "Counter-offer sent to vendor";
  if (["vendor_contacted", "waiting_response", "waiting_vendor", "waiting_on_vendor"].includes(status))
    return "Vendor follow-up if no response";
  return "Evidence packet finalized";
}

function completionEstimate(status: string, daysOpen: number): string {
  if (["accepted", "settled", "completed"].includes(status)) return "Complete";
  if (status === "offer_received") return "Within 3–5 business days";
  if (["negotiating", "in_progress"].includes(status)) return "10–14 business days";
  if (["vendor_contacted", "waiting_response", "waiting_vendor", "waiting_on_vendor"].includes(status))
    return "14–21 business days";
  return daysOpen === 0 ? "3–4 weeks from today" : "3–4 weeks from case opening";
}


/* ─────────────────────────────────────────────────────────── */
/* Meet Your Negotiation Team                                  */
/* ─────────────────────────────────────────────────────────── */

function NegotiationTeamSection({ negotiation }: { negotiation: Negotiation | null }) {
  const assigned = !!negotiation?.assigned_specialist_id;

  return (
    <section className="rounded-3xl border border-border bg-gradient-to-br from-white via-purple-50/30 to-white p-8 shadow-card md:p-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-purple-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.24em] text-purple-700">
            <Users className="h-3.5 w-3.5" />
            Your Team
          </div>
          <h2 className="mt-3 font-display text-3xl leading-tight text-navy md:text-4xl">
            Meet Your Negotiation Team
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Real specialists — not software — advocating for you at every step.
          </p>
        </div>
      </div>

      {assigned ? (
        <div className="mt-8 grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-purple-200 bg-white p-6 shadow-card">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-purple-600 to-purple-700 text-lg font-semibold text-white shadow-sm">
                SM
              </div>
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
                  Assigned Specialist
                </div>
                <div className="mt-1 font-display text-xl text-navy">Sarah Morgan</div>
                <div className="text-sm text-muted-foreground">Senior Negotiation Specialist</div>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 font-semibold text-emerald-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Active on your case
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/50 px-2.5 py-1 font-medium text-navy">
                    <Clock className="h-3 w-3" />
                    Replies within 1 business hour
                  </span>
                </div>
              </div>
            </div>
            <dl className="mt-6 grid grid-cols-3 gap-3 text-center">
              <div className="rounded-xl border border-border bg-secondary/30 p-3">
                <dt className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Experience
                </dt>
                <dd className="mt-1 font-display text-sm text-navy">8+ years</dd>
              </div>
              <div className="rounded-xl border border-border bg-secondary/30 p-3">
                <dt className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Avg. Response
                </dt>
                <dd className="mt-1 font-display text-sm text-navy">42 min</dd>
              </div>
              <div className="rounded-xl border border-border bg-secondary/30 p-3">
                <dt className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Cases Handled
                </dt>
                <dd className="mt-1 font-display text-sm text-navy">340+</dd>
              </div>
            </dl>
          </div>

          <div className="rounded-2xl border border-border bg-white p-6 shadow-card">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-navy to-slate-700 text-lg font-semibold text-white shadow-sm">
                JC
              </div>
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
                  Case Owner
                </div>
                <div className="mt-1 font-display text-xl text-navy">Jordan Chen</div>
                <div className="text-sm text-muted-foreground">Director of Negotiations</div>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-purple-50 px-2.5 py-1 font-semibold text-purple-700">
                    <ShieldCheck className="h-3 w-3" />
                    Oversight & escalation
                  </span>
                </div>
                <p className="mt-3 text-sm text-navy/80">
                  Reviews every settlement proposal before it reaches you, and steps in personally
                  on escalations.
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-8 rounded-2xl border border-dashed border-purple-200 bg-white p-8 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-purple-100 text-purple-700 shadow-sm">
            <UserCircle2 className="h-7 w-7" />
          </div>
          <h3 className="mt-5 font-display text-xl text-navy">
            A senior negotiation specialist will be assigned shortly.
          </h3>
          <p className="mx-auto mt-2 max-w-lg text-sm text-muted-foreground">
            You'll automatically receive an introduction once your specialist is assigned —
            typically within one business day.
          </p>
        </div>
      )}
    </section>
  );
}
